// Imports adicionais para módulo Eventos

/* ──────────────────────────────────────────────────────────────────────
   FESTA JUNINA 2026 — Convite Virtual (DNACore)
   - AdminPanel: lista + busca + edit + stats + export CSV + imprimir
   - CheckIn: leitura de QR/token + validação + check-in parcial
   - ConviteFamilia: visão pública do convite (com QR)
   Tema: roxo/verde/cinza com bandeirinhas Festa Junina.
   ────────────────────────────────────────────────────────────────────── */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Search, Plus, Download, Printer, CheckCircle2, AlertTriangle, XCircle, RefreshCw, QrCode, Users, Trash2, Copy, Edit2, Camera, Eye, EyeOff, Repeat } from 'lucide-react';

const EVENTO = {
  nome: 'Festa Junina 2026',
  tema: 'Sociedade transformada, gestão eficiente',
  data: '20/06/2026',
  horario: '10h00 às 17h00',
  local: 'NOVO DNA - Estrada Benedito Cesário de Oliveira, 156 - Vila Iasi',
  valor: 'R$ 12,00',
  sorteio: 'Bicicleta',
  frase: 'Vamos nos divertir de forma intencional com união e alegria',
};

const checkinUrl = (token) => `${window.location.origin}/festa-junina/checkin/${token}`;

// ──────────────────────────────────────────────────────────────────
// Bandeirinhas SVG (decoração)
// ──────────────────────────────────────────────────────────────────
function Bandeirinhas({ className = '' }) {
  const cores = ['#7B3F98', '#16a34a', '#eab308', '#dc2626', '#0ea5e9'];
  return (
    <svg className={className} viewBox="0 0 600 40" preserveAspectRatio="none" aria-hidden>
      <path d="M0 4 Q300 28 600 4" stroke="#475569" strokeWidth="1.2" fill="none" />
      {Array.from({ length: 20 }).map((_, i) => (
        <g key={i} transform={`translate(${i * 30 + 6}, 8)`}>
          <polygon points="0,0 18,0 9,18" fill={cores[i % cores.length]} />
        </g>
      ))}
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────
// 1) Painel Admin
// ──────────────────────────────────────────────────────────────────
export function FestaJuninaAdminPanel() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [used, setUsed] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const loadStats = async () => {
    try {
      const res = await api.get('/festa-junina/stats');
      setStats(res.data.stats);
    } catch (e) { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      if (used) params.set('used', used);
      params.set('page', String(page));
      params.set('page_size', '50');
      const res = await api.get(`/festa-junina/invites?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Falha ao listar convites.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); load(); /* eslint-disable-next-line */ }, [page]);

  const seed = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/festa-junina/seed');
      toast.success(`Convites prontos: ${res.data.total} (criados agora: ${res.data.created}).`);
      await loadStats(); await load();
    } catch (e) {
      toast.error('Falha ao inicializar convites.');
    } finally { setSeeding(false); }
  };

  const exportar = async () => {
    try {
      const res = await api.get('/festa-junina/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'festa_junina_convites.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Falha ao exportar.'); }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-12">
      <div className="bg-gradient-to-br from-purple-700 via-purple-800 to-emerald-700 text-white">
        <Bandeirinhas className="w-full h-8" />
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="text-xs uppercase tracking-widest text-purple-100">DNACore · {EVENTO.nome}</div>
          <h1 className="text-3xl font-bold mt-1">{EVENTO.nome} — Painel de Convites</h1>
          <p className="text-purple-100 italic mt-1">"{EVENTO.frase}"</p>
        </div>
        <Bandeirinhas className="w-full h-8 rotate-180" />
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-4 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Convites emitidos', stats?.total_emitidos ?? '—', 'bg-white'],
            ['Convidados previstos', stats?.convidados_previstos ?? '—', 'bg-white'],
            ['Convidados presentes', stats?.convidados_presentes ?? '—', 'bg-emerald-50 border-emerald-200'],
            ['Convites utilizados', stats?.convites_utilizados ?? '—', 'bg-purple-50 border-purple-200'],
          ].map(([label, val, cls]) => (
            <div key={label} className={`rounded-lg border p-3 shadow-sm ${cls}`} data-testid={`fj-stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-600">{label}</div>
              <div className="text-2xl font-bold text-slate-800">{val}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg border p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-[260px]">
            <Search className="w-4 h-4 text-slate-500" />
            <Input
              placeholder="Buscar por número, aluno, responsável, turma…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }}
              data-testid="fj-search"
            />
          </div>
          <select className="border rounded px-2 py-2 text-sm" value={used} onChange={(e) => { setUsed(e.target.value); setPage(1); }}>
            <option value="">Todos os usos</option>
            <option value="yes">Já com check-in</option>
            <option value="no">Sem check-in</option>
          </select>
          <select className="border rounded px-2 py-2 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">Todos os status</option>
            <option value="valido">Válidos</option>
            <option value="cancelado">Cancelados</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => { setPage(1); load(); }} className="gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </Button>
          <Button size="sm" onClick={seed} disabled={seeding} className="gap-1 bg-emerald-600 hover:bg-emerald-700">
            {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Inicializar 5000 convites
          </Button>
          <Button size="sm" variant="outline" onClick={exportar} className="gap-1">
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </Button>
          <Link to="/festa-junina/imprimir" target="_blank">
            <Button size="sm" variant="outline" className="gap-1">
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </Button>
          </Link>
        </div>

        {/* Lista */}
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Nº</th>
                <th className="text-left px-3 py-2">Aluno</th>
                <th className="text-left px-3 py-2">Responsável</th>
                <th className="text-left px-3 py-2">Turma</th>
                <th className="text-center px-3 py-2">Limite</th>
                <th className="text-center px-3 py-2">Presentes</th>
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-center px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-6 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-slate-500">Nenhum convite. Clique em "Inicializar 5000 convites".</td></tr>
              )}
              {!loading && items.map((it) => {
                const limite = it.limite_convidados || 0;
                const pres = it.total_checkados || 0;
                const cancelado = it.status === 'cancelado';
                return (
                  <tr key={it.numero} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono font-semibold text-purple-700">#{it.numero_str}</td>
                    <td className="px-3 py-2">{it.aluno_nome || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2">{it.responsavel_nome || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2">{it.turma_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-center">{limite}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={pres > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{pres}/{limite}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {cancelado ? <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700">cancelado</span>
                        : pres >= limite && limite > 0 ? <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">esgotado</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">válido</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Button size="sm" variant="outline" onClick={() => setEditTarget(it)} data-testid={`fj-edit-${it.numero_str}`}>Editar</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="flex items-center justify-between text-sm text-slate-600">
          <div>Total: {total}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
            <span>Página {page}</span>
            <Button size="sm" variant="outline" disabled={page * 50 >= total} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
          </div>
        </div>
      </div>

      {editTarget && (
        <EditInviteDialog
          invite={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); loadStats(); }}
        />
      )}
    </div>
  );
}

function EditInviteDialog({ invite, onClose, onSaved }) {
  const [form, setForm] = useState({
    aluno_nome: invite.aluno_nome || '',
    responsavel_nome: invite.responsavel_nome || '',
    turma_name: invite.turma_name || '',
    limite_convidados: invite.limite_convidados ?? 4,
    status: invite.status || 'valido',
    observacao: invite.observacao || '',
    convidados: (invite.convidados || []).map((c) => c.nome || c).join('\n'),
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        aluno_nome: form.aluno_nome || null,
        responsavel_nome: form.responsavel_nome || null,
        turma_name: form.turma_name || null,
        limite_convidados: Number(form.limite_convidados),
        status: form.status,
        observacao: form.observacao || null,
        convidados: (form.convidados || '')
          .split('\n').map((s) => s.trim()).filter(Boolean).map((nome) => ({ nome })),
      };
      await api.patch(`/festa-junina/invites/${invite.numero}`, payload);
      toast.success('Convite atualizado.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao salvar.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-purple-700">#{invite.numero_str}</span>
            <span>Editar convite</span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Aluno</Label>
            <Input value={form.aluno_nome} onChange={(e) => setForm({ ...form, aluno_nome: e.target.value })} />
          </div>
          <div>
            <Label>Responsável</Label>
            <Input value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} />
          </div>
          <div>
            <Label>Turma</Label>
            <Input value={form.turma_name} onChange={(e) => setForm({ ...form, turma_name: e.target.value })} />
          </div>
          <div>
            <Label>Limite de convidados</Label>
            <Input type="number" min="0" max="99" value={form.limite_convidados} onChange={(e) => setForm({ ...form, limite_convidados: e.target.value })} />
          </div>
          <div>
            <Label>Status</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="valido">Válido</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div className="col-span-2">
            <Label>Convidados (um por linha)</Label>
            <Textarea rows={3} value={form.convidados} onChange={(e) => setForm({ ...form, convidados: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Observação</Label>
            <Textarea rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
          </div>
          <div className="col-span-2 bg-slate-50 rounded p-2 text-xs text-slate-600">
            Token (QR): <span className="font-mono">{invite.token}</span>
            <div className="mt-1">URL: <span className="font-mono">{checkinUrl(invite.token)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <a href={`/festa-junina/convite/${invite.numero}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="gap-1"><QrCode className="w-4 h-4" />Ver convite</Button>
          </a>
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────────
// 2) Check-in (scanner: lê URL com token; sem câmera, abre direto)
// ──────────────────────────────────────────────────────────────────
export function FestaJuninaCheckin() {
  const { token: tokenParam } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(tokenParam || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null); // 'success' | 'error' | null
  const [flashMsg, setFlashMsg] = useState('');
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({ acessos_total: 0, checkados: 0 });
  const [clock, setClock] = useState(new Date());
  const [draw, setDraw] = useState(null); // sorteio ativo
  const [showDraw, setShowDraw] = useState(false);
  const [redoing, setRedoing] = useState(false);
  const [hideValues, setHideValues] = useState(() => {
    try { return localStorage.getItem('fjHideValues') === '1'; } catch { return false; }
  });
  const toggleHideValues = () => {
    setHideValues((v) => {
      const nv = !v;
      try { localStorage.setItem('fjHideValues', nv ? '1' : '0'); } catch { /* noop */ }
      return nv;
    });
  };
  // Câmera QR (modal)
  const [showCamera, setShowCamera] = useState(false);
  const cameraRef = React.useRef(null);
  const lastCamReadRef = React.useRef({ code: '', at: 0 });
  const inputRef = React.useRef(null);

  // Venda Rápida Externa (modal)
  const [quickSale, setQuickSale] = useState(null); // null | { nome, qtd, forma, status, submitting, result, qrUrl }
  const openQuickSale = () => setQuickSale({ nome: '', qtd: 1, forma: 'pix', status: 'pago', submitting: false, result: null, qrUrl: null });
  const submitQuickSale = async () => {
    if (!quickSale) return;
    const nome = (quickSale.nome || '').trim();
    const qtd = Math.max(1, Number(quickSale.qtd) || 1);
    if (!nome) { toast.error('Nome do convidado é obrigatório.'); return; }
    setQuickSale((s) => ({ ...s, submitting: true }));
    try {
      const r = await api.post('/festa-junina/sales', {
        tipo_participante: 'externo',
        visitor_nome: nome,
        quantidade_total: qtd,
        forma_pagamento: quickSale.forma,
        status_pagamento: quickSale.status,
      });
      const sale = r.data?.sale;
      setQuickSale((s) => ({ ...s, submitting: false, result: sale }));
      beep(true);
      toast.success(`Venda criada: ${sale.aluno_nome} · ${sale.quantidade_total} convite(s) · #${String(sale.primeiro_numero).padStart(4,'0')}-${String(sale.ultimo_numero).padStart(4,'0')}`);
      // Refresh stats on background
      loadStats?.();
    } catch (e) {
      setQuickSale((s) => ({ ...s, submitting: false }));
      toast.error(e?.response?.data?.detail || 'Falha ao gerar venda externa.');
    }
  };
  const printQuickSaleQr = () => {
    if (!quickSale?.result) return;
    const sale = quickSale.result;
    const w = window.open('', '_blank', 'width=420,height=620');
    if (!w) { toast.error('Pop-up bloqueado. Permita pop-ups e tente novamente.'); return; }
    const range = `${String(sale.primeiro_numero).padStart(4,'0')}–${String(sale.ultimo_numero).padStart(4,'0')}`;
    // Pega o canvas QR renderizado e converte para dataURL
    let dataUrl = '';
    try {
      const canvas = document.querySelector('[data-testid="fj-quicksale-qr-img"] canvas') || document.querySelector('[data-testid="fj-quicksale-qr-img"]');
      if (canvas && canvas.toDataURL) dataUrl = canvas.toDataURL('image/png');
    } catch { /* noop */ }
    w.document.write(`
      <html><head><title>Convite ${sale.aluno_nome}</title>
      <style>body{font-family:system-ui;text-align:center;padding:20px;}h1{margin:6px 0;font-size:18px;}h2{margin:0;font-size:14px;font-weight:600;color:#555;}img{width:280px;height:280px;}p{font-size:12px;color:#666;margin:4px 0;}</style>
      </head><body>
        <h1>Festa Junina · Novo DNA</h1>
        <h2>${sale.aluno_nome}</h2>
        <p>Convite(s): <strong>${range}</strong> · ${sale.quantidade_total} acesso(s)</p>
        ${dataUrl ? `<img src="${dataUrl}" />` : '<p>QR indisponível para impressão.</p>'}
        <p>Apresente este QR na portaria.</p>
        <script>setTimeout(()=>{window.print();},400);</script>
      </body></html>
    `);
    w.document.close();
  };

  React.useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // BEEP sintetizado (sem dep)
  const beep = (ok = true) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = ok ? 880 : 220;
      g.gain.value = 0.15;
      o.connect(g); g.connect(ctx.destination);
      o.start();
      setTimeout(() => { o.stop(); ctx.close(); }, ok ? 160 : 320);
      if (navigator.vibrate) navigator.vibrate(ok ? 60 : [80, 50, 80]);
    } catch { /* noop */ }
  };

  // Extrai token de URL completa caso o operador cole o link inteiro do QR
  const parseToken = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    const m = s.match(/checkin\/([A-Fa-f0-9]+)/);
    return (m && m[1]) || s;
  };

  const loadStats = async () => {
    try {
      const r = await api.get('/festa-junina/sales/stats');
      setStats(r.data?.stats || {});
    } catch { /* noop */ }
  };
  const loadRecent = async () => {
    try {
      const r = await api.get('/festa-junina/sales?page_size=20');
      const arr = (r.data?.items || []).filter((s) => (s.total_checkados || 0) > 0).slice(0, 8);
      setRecent(arr);
    } catch { /* noop */ }
  };
  const loadDraws = async () => {
    try {
      const r = await api.get('/festa-junina/events/festa-junina-2026/draws');
      const active = r.data?.active_by_prize || {};
      // primeiro prêmio ativo (default Bicicleta)
      const keys = Object.keys(active);
      setDraw(keys.length ? active[keys[0]] : null);
    } catch { /* noop */ }
  };
  const refazerSorteio = async () => {
    if (!window.confirm('Refazer o sorteio agora? O resultado atual será arquivado e um novo número será sorteado entre os check-ins válidos.')) return;
    setRedoing(true);
    try {
      const r = await api.post('/festa-junina/events/festa-junina-2026/sorteio/refazer');
      const d = r.data?.draw;
      if (d) {
        setDraw(d);
        beep(true);
        toast.success(`Novo número sorteado: #${d.ticket_str} — ${d.student_name}`);
      } else {
        await loadDraws();
        toast.success('Sorteio refeito.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao refazer o sorteio.');
    } finally {
      setRedoing(false);
    }
  };
  React.useEffect(() => { loadStats(); loadRecent(); loadDraws(); }, []);

  const triggerFlash = (kind, msg) => {
    setFlash(kind); setFlashMsg(msg);
    setTimeout(() => { setFlash(null); setFlashMsg(''); }, 2500);
  };

  const validate = async (raw) => {
    const t = parseToken(raw);
    if (!t) return;
    setLoading(true); setData(null);
    try {
      // 1) tenta como token de VENDA
      try {
        const r = await api.get(`/festa-junina/sales-validate/${t}`);
        const s = r.data.sale;
        setToken(t);
        setData({
          mode: 'sale',
          numero_str: `${String(s.primeiro_numero).padStart(4,'0')}–${String(s.ultimo_numero).padStart(4,'0')}`,
          aluno_nome: s.aluno_nome, turma_name: s.turma_name, responsavel_nome: s.responsavel_nome,
          limite_convidados: s.quantidade_total, total_checkados: s.total_checkados,
          restantes: s.restantes, estado: s.estado, status: s.status_pagamento,
          checkins: s.checkins,
        });
        return;
      } catch { /* fall through */ }
      // 2) fallback: convite individual
      const res = await api.get(`/festa-junina/validate/${t}`);
      setToken(t);
      setData({ mode: 'invite', ...res.data.invite });
    } catch (e) {
      beep(false);
      triggerFlash('error', e?.response?.data?.detail || 'Convite/QR inválido.');
    } finally { setLoading(false); }
  };

  // Buscar por nome / número quando não for um token (chamada manual via Enter)
  const buscarOuValidar = async () => {
    const v = (query || '').trim();
    if (!v) return;
    // Se for número (1..5000), busca a venda que contém esse número
    if (/^\d{1,4}$/.test(v)) {
      try {
        const num = parseInt(v, 10);
        const r = await api.get(`/festa-junina/sales?page_size=200`);
        const found = (r.data?.items || []).find((s) => (s.numeros || []).includes(num)
          || (num >= (s.primeiro_numero||0) && num <= (s.ultimo_numero||0)));
        if (found?.token) { setQuery(''); return validate(found.token); }
      } catch { /* noop */ }
    }
    // Se parece com token / URL
    if (/^[a-fA-F0-9]{16,}$/.test(v) || /checkin\//.test(v)) {
      setQuery(''); return validate(v);
    }
    // Busca por nome
    try {
      const r = await api.get(`/festa-junina/sales?q=${encodeURIComponent(v)}&page_size=10`);
      const items = r.data?.items || [];
      if (items.length === 1) { setQuery(''); return validate(items[0].token); }
      if (items.length > 1) {
        // mostra como "resultados" — substitui o card de dados temporariamente
        setData({ mode: 'search', results: items });
        return;
      }
      beep(false);
      triggerFlash('error', `Nenhum resultado para "${v}".`);
    } catch (e) {
      triggerFlash('error', 'Falha na busca.');
    }
  };

  useEffect(() => { if (tokenParam) validate(tokenParam); /* eslint-disable-next-line */ }, [tokenParam]);

  const doCheckin = async (count, override = false) => {
    if (!token || !data || data.mode === 'search') return;
    setSubmitting(true);
    try {
      const device = navigator.userAgent;
      const endpoint = data?.mode === 'sale' ? `/festa-junina/sales-checkin/${token}` : `/festa-junina/checkin/${token}`;
      const res = await api.post(endpoint, { count: Number(count), device, override });
      const obj = data?.mode === 'sale' ? res.data.sale : res.data.invite;
      const limite = obj.quantidade_total || obj.limite_convidados;
      const presentes = obj.total_checkados;
      beep(true);
      triggerFlash('success', `LIBERADO · ${data.aluno_nome || '—'} · +${count} (${presentes}/${limite})`);
      loadStats(); loadRecent();
      // Fila ultra-rápida: limpa após 1.6s
      setTimeout(() => {
        setData(null); setToken(''); setQuery('');
        try { inputRef.current && inputRef.current.focus(); } catch { /* noop */ }
      }, 1600);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Falha no check-in.');
      beep(false);
      triggerFlash('error', msg);
    } finally { setSubmitting(false); }
  };

  const restantes = Math.max(0, (data?.restantes ?? data?.limite_convidados ?? 0) - 0);
  const podeCheckin = data && data.mode !== 'search' && data.estado !== 'cancelado' && restantes > 0;

  // Câmera QR — abre/fecha leitor html5-qrcode dentro do modal
  const startCameraScan = async () => {
    setShowCamera(true);
    // aguarda o div do reader montar
    setTimeout(async () => {
      try {
        const mod = await import('html5-qrcode');
        const Html5Qrcode = mod.Html5Qrcode;
        if (!cameraRef.current) cameraRef.current = new Html5Qrcode('fj-checkin-qr-reader');
        if (cameraRef.current.isScanning) return;
        await cameraRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            const now = Date.now();
            if (lastCamReadRef.current.code === decoded && now - lastCamReadRef.current.at < 3500) return;
            lastCamReadRef.current = { code: decoded, at: now };
            // Fecha modal e dispara validação existente
            stopCameraScan().finally(() => {
              setShowCamera(false);
              setQuery(decoded);
              setTimeout(() => buscarOuValidar(), 50);
            });
          },
          () => {},
        );
      } catch (err) {
        toast.error(`Câmera indisponível: ${err?.message || err}`);
        setShowCamera(false);
      }
    }, 200);
  };

  const stopCameraScan = async () => {
    try {
      if (cameraRef.current?.isScanning) {
        await cameraRef.current.stop();
      }
    } catch (_) { /* noop */ }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-white to-purple-50 flex flex-col" data-testid="fj-portaria">
      {/* FLASH overlay */}
      {flash && (
        <div className={`absolute inset-0 z-50 flex items-center justify-center text-white text-center px-4 pointer-events-none animate-fade ${flash === 'success' ? 'bg-emerald-600/95' : 'bg-rose-600/95'}`}>
          <div>
            <div className="text-8xl font-black tracking-tight mb-2">{flash === 'success' ? '✅' : '❌'}</div>
            <div className="text-5xl font-black uppercase">{flash === 'success' ? 'LIBERADO' : 'INVÁLIDO'}</div>
            <div className="text-xl mt-2 opacity-95 max-w-2xl mx-auto">{flashMsg}</div>
          </div>
        </div>
      )}

      {/* TOPO FIXO */}
      <header className="px-6 py-3 bg-gradient-to-r from-purple-700 to-emerald-700 text-white flex items-center gap-4 shadow">
        <div className="font-extrabold text-2xl tracking-tight flex items-center gap-2">
          <span className="bg-white text-purple-700 w-9 h-9 rounded inline-flex items-center justify-center font-black">D</span>
          Novo DNA <span className="opacity-70 text-base font-medium">· Portaria</span>
        </div>
        <div className="ml-2 text-sm border-l border-white/30 pl-3">
          <div className="text-[10px] uppercase tracking-widest opacity-80">Evento</div>
          <div className="font-semibold">{EVENTO.nome}</div>
        </div>
        <div className="ml-auto flex items-center gap-5">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest opacity-80">Presentes</div>
            <div className="text-2xl font-extrabold leading-none">{stats.checkados || 0}<span className="text-base opacity-70"> / {stats.acessos_total || 0}</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest opacity-80">Hora</div>
            <div className="text-2xl font-mono font-bold leading-none">{clock.toLocaleTimeString('pt-BR')}</div>
          </div>
          <Button
            variant="outline"
            className="bg-emerald-500/90 border-emerald-300 text-white hover:bg-emerald-600 gap-2 font-semibold"
            onClick={openQuickSale}
            title="Venda rápida para público externo (gera QR na hora)"
            data-testid="fj-portaria-quicksale-btn"
          >
            <Plus className="w-4 h-4" /> Venda Rápida Externa
          </Button>
          <Button
            variant="outline"
            className="bg-white/10 border-white/40 text-white hover:bg-white/20 gap-2"
            onClick={toggleHideValues}
            title={hideValues ? 'Mostrar valores financeiros' : 'Ocultar valores financeiros'}
            data-testid="fj-portaria-toggle-values"
          >
            {hideValues ? <><EyeOff className="w-4 h-4" /> Valores ocultos</> : <><Eye className="w-4 h-4" /> Ocultar valores</>}
          </Button>
          <Button variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20" onClick={() => navigate('/v2/festa-junina')} data-testid="fj-portaria-painel">
            Ir para o painel V2
          </Button>
        </div>
      </header>

      {/* CORPO */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 overflow-hidden">
        {/* CENTRO: leitura */}
        <div className="lg:col-span-2 flex flex-col gap-4 min-h-0">
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Leitura</div>
            <div className="flex gap-2 items-center">
              <input
                ref={inputRef}
                autoFocus
                value={query || token}
                onChange={(e) => { setQuery(e.target.value); setToken(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') buscarOuValidar(); }}
                placeholder="Aponte o QR, digite o número (ex: 0123) ou nome do aluno…"
                className="flex-1 text-2xl px-4 py-4 border-2 border-purple-300 rounded-xl outline-none focus:border-purple-600 font-medium"
                data-testid="fj-portaria-input"
              />
              <Button onClick={buscarOuValidar} disabled={loading} className="h-14 px-6 text-lg bg-purple-600 hover:bg-purple-700 text-white gap-2" data-testid="fj-portaria-validar">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} Validar
              </Button>
              <Button onClick={startCameraScan} disabled={loading} className="h-14 px-5 text-lg bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="fj-portaria-camera-open">
                <Camera className="w-5 h-5" />Câmera
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">
              Dica: a câmera do celular abre o link do QR direto na URL <span className="font-mono">/v2/festa-junina/checkin/&lt;token&gt;</span>. Aqui você pode digitar número, nome ou colar o link.
            </div>
          </div>

          {/* Modal câmera QR */}
          {showCamera && (
            <Dialog open={true} onOpenChange={(o) => { if (!o) { stopCameraScan(); setShowCamera(false); } }}>
              <DialogContent className="max-w-md bg-slate-900 text-white border-slate-700">
                <DialogHeader><DialogTitle className="text-white">Leitor de QR Code</DialogTitle></DialogHeader>
                <div id="fj-checkin-qr-reader" className="bg-black rounded-lg overflow-hidden min-h-[320px]" data-testid="fj-camera-reader" />
                <div className="text-xs text-slate-300">Aponte para o QR Code do convite. A leitura é automática.</div>
                <DialogFooter>
                  <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 bg-slate-800" onClick={() => { stopCameraScan(); setShowCamera(false); }} data-testid="fj-camera-close">
                    Fechar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Modal Venda Rápida Externa */}
          {quickSale && (
            <Dialog open={true} onOpenChange={(o) => { if (!o && !quickSale.submitting) setQuickSale(null); }}>
              <DialogContent className="max-w-md bg-white">
                <DialogHeader>
                  <DialogTitle className="text-emerald-700 flex items-center gap-2">
                    <Plus className="w-5 h-5" /> Venda Rápida — Público Externo
                  </DialogTitle>
                </DialogHeader>
                {!quickSale.result ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Nome do convidado *</Label>
                      <Input
                        autoFocus
                        value={quickSale.nome}
                        onChange={(e) => setQuickSale((s) => ({ ...s, nome: e.target.value }))}
                        placeholder="Ex.: Maria Souza"
                        data-testid="fj-quicksale-nome"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Convites</Label>
                        <Input
                          type="number" min="1" max="50"
                          value={quickSale.qtd}
                          onChange={(e) => setQuickSale((s) => ({ ...s, qtd: e.target.value }))}
                          data-testid="fj-quicksale-qtd"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Forma de pagamento</Label>
                        <select
                          className="w-full border rounded-md px-2 py-2 text-sm bg-white"
                          value={quickSale.forma}
                          onChange={(e) => setQuickSale((s) => ({ ...s, forma: e.target.value }))}
                          data-testid="fj-quicksale-forma"
                        >
                          <option value="pix">PIX</option>
                          <option value="dinheiro">Dinheiro</option>
                          <option value="cartao_credito">Cartão Crédito</option>
                          <option value="cartao_debito">Cartão Débito</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Status do pagamento</Label>
                      <select
                        className="w-full border rounded-md px-2 py-2 text-sm bg-white"
                        value={quickSale.status}
                        onChange={(e) => setQuickSale((s) => ({ ...s, status: e.target.value }))}
                        data-testid="fj-quicksale-status"
                      >
                        <option value="pago">Pago</option>
                        <option value="pendente">Pendente</option>
                      </select>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Sem gratuidade · numeração contínua de convites · QR gerado na hora pra leitura imediata na catraca.
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="fj-quicksale-result">
                    <div className="rounded-lg border bg-emerald-50 border-emerald-300 p-3 text-center">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700">Venda gerada</div>
                      <div className="font-extrabold text-lg text-emerald-800 mt-1">{quickSale.result.aluno_nome}</div>
                      <div className="font-mono text-sm text-slate-700">#{String(quickSale.result.primeiro_numero).padStart(4,'0')}–#{String(quickSale.result.ultimo_numero).padStart(4,'0')} · {quickSale.result.quantidade_total} acesso(s) · R$ {Number(quickSale.result.valor_total||0).toFixed(2)}</div>
                    </div>
                    <div className="flex justify-center" data-testid="fj-quicksale-qr-img">
                      <QRCodeCanvas value={saleCheckinUrl(quickSale.result.token)} size={224} level="M" includeMargin />
                    </div>
                  </div>
                )}
                <DialogFooter>
                  {!quickSale.result ? (
                    <>
                      <Button variant="outline" onClick={() => setQuickSale(null)} disabled={quickSale.submitting}>Cancelar</Button>
                      <Button
                        onClick={submitQuickSale}
                        disabled={quickSale.submitting || !(quickSale.nome || '').trim()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                        data-testid="fj-quicksale-confirm"
                      >
                        {quickSale.submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                        {quickSale.submitting ? 'Gerando…' : 'Gerar venda + QR'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setQuickSale(null)} data-testid="fj-quicksale-close">Fechar</Button>
                      <Button variant="outline" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={openQuickSale} data-testid="fj-quicksale-nova">
                        <Plus className="w-4 h-4" /> Nova venda
                      </Button>
                      <Button onClick={printQuickSaleQr} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="fj-quicksale-print">
                        <Printer className="w-4 h-4" /> Imprimir
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* DADOS DA VENDA */}
          {data && data.mode === 'search' && (
            <div className="bg-white border rounded-2xl p-4 space-y-2" data-testid="fj-portaria-search">
              <div className="text-xs uppercase text-slate-500">Resultados</div>
              {(data.results || []).map((s) => (
                <button key={s.id}
                  onClick={() => { setData(null); validate(s.token); }}
                  className="w-full text-left px-3 py-2 border rounded-lg hover:bg-slate-50 flex justify-between"
                >
                  <span className="font-semibold">{s.aluno_nome} <span className="text-slate-500 font-normal">· {s.turma_name || '—'}</span></span>
                  <span className="font-mono text-purple-700 text-sm">#{String(s.primeiro_numero).padStart(4,'0')}–#{String(s.ultimo_numero).padStart(4,'0')} · {s.total_checkados}/{s.quantidade_total}</span>
                </button>
              ))}
            </div>
          )}

          {data && data.mode !== 'search' && (
            <div className="bg-white border-4 border-emerald-200 rounded-2xl p-5 flex-1 min-h-0 overflow-y-auto" data-testid="fj-portaria-card">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs uppercase text-slate-500">Convite</div>
                  <div className="text-5xl font-extrabold uppercase truncate text-slate-900">{data.aluno_nome || '—'}</div>
                  <div className="text-xl text-slate-600 mt-1">{data.turma_name || '—'}{data.responsavel_nome ? ` · ${data.responsavel_nome}` : ''}</div>
                  <div className="text-base font-mono text-purple-700 mt-1">#{data.numero_str}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase text-slate-500">Restam</div>
                  <div className={`text-7xl font-black leading-none ${restantes <= 0 ? 'text-slate-400' : 'text-emerald-600'}`}>{restantes}</div>
                  <div className="text-sm text-slate-500">de {data.limite_convidados} acessos</div>
                </div>
              </div>

              {/* botões parciais GIGANTES */}
              <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[1, 2, 3].filter((n) => n <= restantes).map((n) => (
                  <Button
                    key={n}
                    onClick={() => doCheckin(n)}
                    disabled={submitting || !podeCheckin}
                    className="h-20 text-3xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold"
                    data-testid={`fj-portaria-plus-${n}`}
                  >
                    +{n}
                  </Button>
                ))}
                {restantes > 0 && (
                  <Button
                    onClick={() => doCheckin(restantes)}
                    disabled={submitting || !podeCheckin}
                    className="h-20 text-xl bg-purple-700 hover:bg-purple-800 text-white font-extrabold col-span-2 md:col-span-1"
                    data-testid="fj-portaria-total"
                  >
                    ENTRADA TOTAL (+{restantes})
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => { setData(null); setToken(''); setQuery(''); try { inputRef.current?.focus(); } catch { /* noop */ } }}
                  className="h-20 text-base"
                  data-testid="fj-portaria-cancelar"
                >
                  Cancelar
                </Button>
              </div>

              {/* Status secundário */}
              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                {data.estado === 'cancelado' && (<span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 px-3 py-1 rounded border border-rose-200"><XCircle className="w-4 h-4" /> Cancelado</span>)}
                {data.estado === 'esgotado' && (<span className="inline-flex items-center gap-1 text-slate-700 bg-slate-100 px-3 py-1 rounded border border-slate-200"><AlertTriangle className="w-4 h-4" /> Esgotado</span>)}
                {data.estado === 'parcial' && (<span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-3 py-1 rounded border border-amber-200"><AlertTriangle className="w-4 h-4" /> Em uso</span>)}
                {data.estado === 'valido' && (<span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-3 py-1 rounded border border-emerald-200"><CheckCircle2 className="w-4 h-4" /> Válido</span>)}
                {data.status && data.status !== 'pago' && data.status !== 'cortesia' && (
                  <span className="inline-flex items-center gap-1 text-amber-800 bg-amber-50 px-3 py-1 rounded border border-amber-200">Pagamento: {data.status}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* LATERAL DIREITA */}
        <aside className="bg-white border rounded-2xl p-4 flex flex-col min-h-0">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2"><div className="text-[10px] uppercase text-slate-500">Presentes</div><div className="text-2xl font-extrabold text-emerald-700">{stats.checkados || 0}</div></div>
            <div className="rounded-lg bg-slate-50 border p-2"><div className="text-[10px] uppercase text-slate-500">Restantes</div><div className="text-2xl font-extrabold text-slate-700">{Math.max(0, (stats.acessos_total||0) - (stats.checkados||0))}</div></div>
            {!hideValues && (<>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2" data-testid="fj-portaria-pendentes"><div className="text-[10px] uppercase text-slate-500">Pendentes (R$)</div><div className="text-xl font-extrabold text-amber-700">R$ {(stats.valor_pendente||0).toFixed(2)}</div></div>
              <div className="rounded-lg bg-purple-50 border border-purple-200 p-2" data-testid="fj-portaria-pagos"><div className="text-[10px] uppercase text-slate-500">Pagos (R$)</div><div className="text-xl font-extrabold text-purple-700">R$ {(stats.valor_pago||0).toFixed(2)}</div></div>
            </>)}
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Últimos liberados</div>
          <div className="overflow-y-auto flex-1 space-y-1" data-testid="fj-portaria-recent">
            {recent.length === 0 && (<div className="text-sm text-slate-400 py-6 text-center">Nenhum check-in ainda.</div>)}
            {recent.map((s) => (
              <div key={s.id} className="flex items-center justify-between border rounded-lg px-2 py-1.5 bg-emerald-50/40">
                <div className="min-w-0">
                  <div className="font-semibold truncate text-sm">✅ {s.aluno_nome}</div>
                  <div className="text-[11px] text-slate-500 truncate">{s.turma_name || '—'} · {s.total_checkados}/{s.quantidade_total}</div>
                </div>
                <div className="text-[10px] font-mono text-purple-700">#{String(s.primeiro_numero).padStart(4,'0')}</div>
              </div>
            ))}
          </div>
          <Button variant="outline" className="mt-3" onClick={() => loadRecent()}><RefreshCw className="w-3.5 h-3.5 mr-1" />Atualizar</Button>

          {/* SORTEIO */}
          <div className="mt-3 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-3" data-testid="fj-portaria-sorteio-card">
            <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-1">🎁 Sorteio</div>
            <div className="text-base font-bold">{draw?.prize_name || 'Bicicleta'}</div>
            {draw ? (
              <div className="mt-1">
                <div className="text-3xl font-mono font-extrabold text-amber-700 leading-tight">#{draw.ticket_str}</div>
                <div className="text-sm font-semibold text-slate-800 truncate">{draw.student_name}</div>
                <div className="text-[11px] text-slate-600 truncate">{draw.class_group_name || '—'}</div>
                <div className="text-[10px] text-emerald-700 font-semibold uppercase mt-1">Realizado</div>
              </div>
            ) : (
              <div className="text-[11px] text-slate-600 mt-1">Status: pendente</div>
            )}
            <Button onClick={() => setShowDraw(true)} className="w-full mt-2 bg-amber-500 hover:bg-amber-600 text-white font-bold" data-testid="fj-portaria-sorteio-btn">
              {draw ? 'Abrir sorteio' : 'Sorteio da Bicicleta'}
            </Button>
            {draw && (
              <Button
                onClick={refazerSorteio}
                disabled={redoing}
                variant="outline"
                className="w-full mt-2 border-rose-300 text-rose-700 hover:bg-rose-50 gap-2"
                data-testid="fj-portaria-sorteio-refazer"
              >
                {redoing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
                {redoing ? 'Refazendo…' : 'Refazer sorteio'}
              </Button>
            )}
          </div>
        </aside>
      </div>

      <style>{`@keyframes fade { from{opacity:0;transform:scale(.92)} to{opacity:1;transform:scale(1)} } .animate-fade { animation: fade .15s ease-out } `}</style>
      {showDraw && (
        <SorteioDialog
          existing={draw}
          onClose={() => setShowDraw(false)}
          onDrawn={(d) => { setDraw(d); }}
        />
      )}
    </div>
  );
}

function SorteioDialog({ existing, onClose, onDrawn }) {
  const [prize, setPrize] = React.useState(existing?.prize_name || 'Bicicleta');
  const [onlyChecked, setOnlyChecked] = React.useState(existing?.only_checked_in || false);
  const [reason, setReason] = React.useState('');
  const [rolling, setRolling] = React.useState(false);
  const [rollNum, setRollNum] = React.useState(null);
  const [result, setResult] = React.useState(existing || null);

  const realizar = async (redo = false) => {
    if (redo && !reason.trim()) { toast.error('Motivo obrigatório para refazer.'); return; }
    setRolling(true); setResult(null);
    // animação de números girando ~1.6s
    const iv = setInterval(() => setRollNum(String(Math.floor(Math.random() * 5000) + 1).padStart(4, '0')), 60);
    try {
      const res = await api.post('/festa-junina/events/festa-junina-2026/draw', {
        prize_name: prize, only_checked_in: onlyChecked, redo, reason: reason || null,
      });
      const d = res.data?.draw;
      await new Promise((r) => setTimeout(r, 1600));
      clearInterval(iv);
      setRollNum(d?.ticket_str || null);
      setResult(d);
      onDrawn(d);
    } catch (e) {
      clearInterval(iv);
      toast.error(e?.response?.data?.detail || 'Falha no sorteio.');
    } finally { setRolling(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">🎁 Sorteio — {EVENTO.nome}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Prêmio</Label>
              <Input value={prize} onChange={(e) => setPrize(e.target.value)} disabled={rolling} data-testid="fj-sorteio-prize" />
            </div>
            <label className="inline-flex items-center gap-1 text-xs pb-2">
              <input type="checkbox" checked={onlyChecked} onChange={(e) => setOnlyChecked(e.target.checked)} disabled={rolling} />
              Somente com check-in
            </label>
          </div>

          <div className={`rounded-xl border-4 ${result ? 'border-emerald-400 bg-emerald-50' : 'border-amber-300 bg-amber-50'} p-6 text-center`}>
            <div className="text-xs uppercase tracking-widest text-slate-600">Número sorteado</div>
            <div className={`text-7xl font-mono font-black ${result ? 'text-emerald-700' : 'text-amber-700'} ${rolling ? 'animate-pulse' : ''}`} data-testid="fj-sorteio-num">
              {rolling || result ? `#${rollNum || result?.ticket_str || '0000'}` : '#----'}
            </div>
            {result && !rolling && (
              <div className="mt-2 space-y-0.5">
                <div className="text-2xl font-extrabold uppercase">{result.student_name || '—'}</div>
                <div className="text-sm text-slate-700">{result.class_group_name || '—'}</div>
                {result.responsavel_nome && <div className="text-xs text-slate-500">Responsável: {result.responsavel_nome}</div>}
                <div className="text-[10px] text-slate-500 mt-1">Universo: {result.universe_size} tickets · {new Date(result.drawn_at).toLocaleString('pt-BR')}{result.is_redo ? ' · refeito' : ''}</div>
              </div>
            )}
          </div>

          {existing && (
            <div className="border rounded p-2 bg-slate-50 text-xs">
              <div className="font-semibold mb-1">Refazer sorteio (apenas admin) — motivo obrigatório:</div>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: contestação por irregularidade." disabled={rolling} data-testid="fj-sorteio-reason" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={rolling}>Fechar</Button>
          {!existing || result ? (
            <Button onClick={() => realizar(false)} disabled={rolling} className="bg-amber-500 hover:bg-amber-600 text-white font-bold" data-testid="fj-sorteio-do">
              {rolling ? 'Sorteando…' : (existing ? 'Sortear novamente' : 'Realizar sorteio')}
            </Button>
          ) : (
            <Button onClick={() => realizar(true)} disabled={rolling || !reason.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="fj-sorteio-redo">
              {rolling ? 'Refazendo…' : 'Refazer sorteio (admin)'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────────
// 3) Convite (família) — visual + QR
// ──────────────────────────────────────────────────────────────────
export function FestaJuninaConvite() {
  const { numero } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/festa-junina/invites/${numero}`);
        setData(res.data.invite);
      } catch (e) {
        toast.error('Convite não encontrado.');
      } finally { setLoading(false); }
    })();
  }, [numero]);

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /> Carregando…</div>;
  if (!data) return <div className="p-8 text-center text-slate-600">Convite não encontrado.</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-6 print:bg-white print:py-0">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg overflow-hidden border print:shadow-none print:border-0">
        <div className="bg-gradient-to-br from-purple-700 to-emerald-700 text-white px-5 py-4">
          <Bandeirinhas className="w-full h-6 -mt-2 -mx-2" />
          <div className="text-[11px] uppercase tracking-widest text-purple-100">DNACore · Festa Junina</div>
          <div className="text-2xl font-bold mt-0.5">{EVENTO.nome}</div>
          <div className="text-xs text-purple-100 italic mt-0.5">"{EVENTO.frase}"</div>
        </div>
        <div className="px-5 py-4 space-y-2 text-sm">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[11px] uppercase text-slate-500">Convite</div>
              <div className="text-4xl font-mono font-bold text-purple-700">#{data.numero_str}</div>
            </div>
            <div className="bg-white p-1 border rounded">
              <QRCodeCanvas value={checkinUrl(data.token)} size={120} level="M" includeMargin={false} />
            </div>
          </div>
          <div><strong>Tema:</strong> {EVENTO.tema}</div>
          <div><strong>Data:</strong> {EVENTO.data} · {EVENTO.horario}</div>
          <div><strong>Local:</strong> {EVENTO.local}</div>
          <div><strong>Valor:</strong> {EVENTO.valor} · <strong>Sorteio:</strong> {EVENTO.sorteio}</div>
          {data.aluno_nome && <div className="mt-1 pt-2 border-t"><strong>Aluno:</strong> {data.aluno_nome}{data.turma_name ? ` · ${data.turma_name}` : ''}</div>}
          {data.responsavel_nome && <div><strong>Responsável:</strong> {data.responsavel_nome}</div>}
          <div className="flex items-center gap-2 text-emerald-700 pt-1">
            <Users className="w-4 h-4" />
            <span>{data.limite_convidados} convidado(s) autorizado(s) · {data.total_checkados} presente(s)</span>
          </div>
          {(data.convidados || []).length > 0 && (
            <div className="text-xs text-slate-600 pt-1">
              <div className="font-medium">Convidados:</div>
              <ul className="list-disc list-inside">
                {data.convidados.map((c, i) => (<li key={i}>{c.nome || c}</li>))}
              </ul>
            </div>
          )}
        </div>
        <Bandeirinhas className="w-full h-6 rotate-180" />
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// 4) Impressão A4 (4 por folha) — todos do filtro atual
// ──────────────────────────────────────────────────────────────────
export function FestaJuninaImprimir() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/festa-junina/invites?page_size=500&page=1');
        setItems((res.data.items || []).filter((it) => it.aluno_nome));
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { if (!loading) setTimeout(() => window.print(), 500); }, [loading]);

  if (loading) return <div className="p-8">Carregando…</div>;

  return (
    <div className="bg-white">
      <style>{`
        @page { size: A4; margin: 8mm; }
        .sheet { display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; }
        .card { border: 1px dashed #94a3b8; border-radius: 8px; padding: 8px; page-break-inside: avoid; }
        .pb { page-break-after: always; }
      `}</style>
      {Array.from({ length: Math.ceil(items.length / 4) }).map((_, pageIdx) => (
        <div key={pageIdx} className={`sheet ${pageIdx < Math.ceil(items.length / 4) - 1 ? 'pb' : ''}`}>
          {items.slice(pageIdx * 4, pageIdx * 4 + 4).map((it) => (
            <div key={it.numero} className="card">
              <div className="flex justify-between items-start">
                <div>
                  <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Festa Junina · {EVENTO.data}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 26, fontWeight: 700, color: '#7B3F98' }}>#{it.numero_str}</div>
                  <div style={{ fontSize: 11 }}><strong>{it.aluno_nome}</strong>{it.turma_name ? ` · ${it.turma_name}` : ''}</div>
                  <div style={{ fontSize: 10, color: '#475569' }}>Resp.: {it.responsavel_nome || '—'}</div>
                  <div style={{ fontSize: 10, color: '#475569' }}>{it.limite_convidados} convidado(s)</div>
                  <div style={{ fontSize: 9, color: '#0f766e', marginTop: 4, fontStyle: 'italic' }}>"{EVENTO.frase}"</div>
                </div>
                <QRCodeCanvas value={checkinUrl(it.token)} size={90} level="M" />
              </div>
              <div style={{ fontSize: 9, color: '#475569', marginTop: 4, borderTop: '1px solid #e2e8f0', paddingTop: 4 }}>
                {EVENTO.local} · {EVENTO.horario} · {EVENTO.valor}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}



// ──────────────────────────────────────────────────────────────────
// VENDAS POR ALUNO (V2) — registro + lista + QR + check-in + imprimir
// ──────────────────────────────────────────────────────────────────
const saleCheckinUrl = (token) => `${window.location.origin}/v2/festa-junina/checkin/${token}`;

const FORMAS = ['pix', 'dinheiro', 'cartao', 'cortesia', 'outro'];
const STATUS = ['pendente', 'pago', 'cortesia', 'cancelado'];
// Fichas Antecipadas (bloco separado dos convites)
const FICHAS_PAG_STATUS = ['pendente', 'pago', 'cancelado'];
const FICHAS_ENT_STATUS = ['nao_entregue', 'entregue'];
const fichasEntLabel = (v) => (v === 'entregue' ? 'Entregue' : 'Não entregue');

export function FestaJuninaVendas() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventoId, setEventoId] = useState('');
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [sales, setSales] = useState([]);
  const [salesTotal, setSalesTotal] = useState(0);
  const [filterTurma, setFilterTurma] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [filterTipo, setFilterTipo] = useState(''); // '' | 'aluno' | 'externo'
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  // Form da Nova Venda
  const [turmaId, setTurmaId] = useState('');
  const [alunoId, setAlunoId] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [forma, setForma] = useState('pix');
  const [statusPg, setStatusPg] = useState('pendente');
  const [responsavel, setResponsavel] = useState('');
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [qrTarget, setQrTarget] = useState(null);
  const [printTarget, setPrintTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  // Fichas Antecipadas (bloco isolado)
  const [fichasTarget, setFichasTarget] = useState(null); // { sale } edit/anexa em venda existente
  const [openFichasOnly, setOpenFichasOnly] = useState(false); // criar venda só de fichas
  // Gratuidade família +1 filho
  const [gratuidadeExtra, setGratuidadeExtra] = useState(0);
  const [familyInfo, setFamilyInfo] = useState(null); // { total_familia, irmaos:[] }
  // Tipo participante (aluno | externo)
  const [tipoParticipante, setTipoParticipante] = useState('aluno');
  const [extNome, setExtNome] = useState('');
  const [extCpf, setExtCpf] = useState('');
  const [extTelefone, setExtTelefone] = useState('');
  const [extEmail, setExtEmail] = useState('');

  const turmaSel = useMemo(() => classes.find((c) => c.id === turmaId), [classes, turmaId]);
  const alunoSel = useMemo(() => students.find((s) => s.id === alunoId), [students, alunoId]);

  const loadAll = async () => {
    try {
      const [st, cl, ev] = await Promise.all([
        api.get(`/festa-junina/sales/stats${filterEvent ? `?event_id=${filterEvent}` : ''}`),
        api.get('/class-groups'),
        api.get('/festa-junina/events'),
      ]);
      setStats(st.data);
      setClasses(cl.data || []);
      const evs = ev.data?.items || [];
      setEvents(evs);
      if (!eventoId && evs.length) setEventoId(evs[0].id);
    } catch (e) { /* noop */ }
  };
  const loadSales = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (filterTurma) p.set('turma_id', filterTurma);
      if (filterStatus) p.set('status_pagamento', filterStatus);
      if (filterEvent) p.set('event_id', filterEvent);
      if (filterTipo) p.set('tipo_participante', filterTipo);
      p.set('page_size', '100');
      const res = await api.get(`/festa-junina/sales?${p.toString()}`);
      setSales(res.data.items || []); setSalesTotal(res.data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); loadSales(); }, []); // eslint-disable-line
  useEffect(() => { loadAll(); loadSales(); }, [filterTurma, filterStatus, filterEvent, filterTipo]); // eslint-disable-line

  // carrega alunos quando turma mudar
  useEffect(() => {
    setAlunoId(''); // ao trocar turma, limpa o aluno selecionado
    if (!turmaId) { setStudents([]); return; }
    (async () => {
      try {
        const res = await api.get(`/students?class_id=${turmaId}&active=true&page_size=500`);
        const arr = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.students || []);
        setStudents(arr);
      } catch (e) { setStudents([]); }
    })();
  }, [turmaId]);

  // Buscar info de família ao selecionar aluno (irmãos ativos)
  useEffect(() => {
    setFamilyInfo(null); setGratuidadeExtra(0);
    if (!alunoId) return;
    let cancel = false;
    (async () => {
      try {
        const res = await api.get(`/festa-junina/familia/irmaos?aluno_id=${alunoId}`);
        if (!cancel) setFamilyInfo(res.data);
      } catch (e) { if (!cancel) setFamilyInfo(null); }
    })();
    return () => { cancel = true; };
  }, [alunoId]);

  // preview ao mudar quantidade
  useEffect(() => {
    if (!alunoId || !turmaId || quantidade < 1) { setPreview(null); return; }
    let cancel = false;
    (async () => {
      try {
        const res = await api.post('/festa-junina/sales/preview', {
          aluno_id: alunoSel?.id, aluno_nome: alunoSel?.name,
          turma_id: turmaSel?.id, turma_name: turmaSel?.name,
          quantidade_total: Number(quantidade),
          quantidade_gratuita_extra: Number(gratuidadeExtra) || 0,
        });
        if (!cancel) setPreview(res.data);
      } catch (e) { if (!cancel) setPreview(null); }
    })();
    return () => { cancel = true; };
  }, [alunoId, turmaId, quantidade, alunoSel, turmaSel, gratuidadeExtra]);

  const gerarVenda = async () => {
    if (tipoParticipante === 'aluno') {
      if (!turmaId || !alunoId) { toast.error('Selecione turma e aluno.'); return; }
    } else {
      if (!extNome.trim()) { toast.error('Nome do convidado é obrigatório.'); return; }
    }
    if (quantidade < 1) { toast.error('Quantidade inválida.'); return; }
    setCreating(true);
    try {
      const payload = tipoParticipante === 'aluno' ? {
        event_id: eventoId || undefined,
        tipo_participante: 'aluno',
        aluno_id: alunoSel?.id, aluno_nome: alunoSel?.name,
        turma_id: turmaSel?.id, turma_name: turmaSel?.name,
        responsavel_nome: responsavel || null,
        quantidade_total: Number(quantidade),
        quantidade_gratuita_extra: Number(gratuidadeExtra) || 0,
        forma_pagamento: forma,
        status_pagamento: statusPg,
      } : {
        event_id: eventoId || undefined,
        tipo_participante: 'externo',
        visitor_nome: extNome.trim(),
        visitor_cpf: extCpf.trim() || null,
        visitor_telefone: extTelefone.trim(),
        visitor_email: extEmail.trim() || null,
        responsavel_nome: responsavel || null,
        quantidade_total: Number(quantidade),
        forma_pagamento: forma,
        status_pagamento: statusPg,
      };
      const res = await api.post('/festa-junina/sales', payload);
      const s = res.data.sale;
      toast.success(`Venda criada: ${s.aluno_nome} · ${s.quantidade_total} acessos · números ${String(s.primeiro_numero).padStart(4,'0')}-${String(s.ultimo_numero).padStart(4,'0')} · R$ ${(s.valor_total).toFixed(2)}`);
      setAlunoId(''); setQuantidade(1); setPreview(null); setResponsavel(''); setGratuidadeExtra(0); setFamilyInfo(null);
      setExtNome(''); setExtCpf(''); setExtTelefone(''); setExtEmail('');
      await loadAll(); await loadSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao gerar venda.');
    } finally { setCreating(false); }
  };

  const setStatus = async (sale, novo) => {
    try {
      await api.patch(`/festa-junina/sales/${sale.id}`, { status_pagamento: novo });
      toast.success('Status atualizado.');
      await loadAll(); await loadSales();
    } catch (e) { toast.error('Falha ao atualizar.'); }
  };

  const cancelSale = async (sale) => {
    if (!window.confirm(`Cancelar venda de ${sale.aluno_nome}? Os números ${String(sale.primeiro_numero).padStart(4,'0')}-${String(sale.ultimo_numero).padStart(4,'0')} ficarão inutilizados (não podem ser reaproveitados). QR será invalidado.`)) return;
    try {
      await api.delete(`/festa-junina/sales/${sale.id}`);
      toast.success('Venda cancelada. QR invalidado.');
      await loadAll(); await loadSales();
    } catch (e) { toast.error('Falha ao cancelar.'); }
  };

  const hardDeleteSale = async (sale) => {
    if (!window.confirm(`EXCLUIR DEFINITIVO de ${sale.aluno_nome}?\nVai zerar venda + financeiro + check-ins deste lote.\nOs números seguirão queimados (não retornam ao pool).`)) return;
    try {
      await api.delete(`/festa-junina/sales/${sale.id}/hard`);
      toast.success('Venda excluída.');
      await loadAll(); await loadSales();
    } catch (e) { toast.error('Falha ao excluir.'); }
  };

  const togglePublicarConnect = async (sale) => {
    const novo = !(sale.publicado_dna_connect === true || sale.publicado_dna_connect == null);
    try {
      await api.post(`/festa-junina/sales/${sale.id}/publicar`, { publicado: novo });
      toast.success(novo ? 'Publicado no DNA Connect.' : 'Ocultado do DNA Connect.');
      await loadSales();
    } catch (e) { toast.error('Falha ao atualizar visibilidade.'); }
  };

  const resetEventoCompleto = async () => {
    if (!window.confirm('ATENÇÃO: isso vai EXCLUIR TODAS as vendas do evento atual e zerar a numeração global (se for o evento padrão).\nEsta ação não pode ser desfeita. Confirma?')) return;
    try {
      const evId = filterEvent || eventoId || 'festa-junina-2026';
      await api.post(`/festa-junina/events/${evId}/reset`);
      toast.success('Evento resetado.');
      await loadAll(); await loadSales();
    } catch (e) { toast.error('Falha ao resetar.'); }
  };

  const exportar = async () => {
    try {
      const res = await api.get('/festa-junina/sales-export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'festa_junina_vendas.csv'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Falha ao exportar.'); }
  };

  // Query string com filtros atuais (compartilhada por Excel/PDF)
  const buildFiltersQS = () => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (filterTurma) p.set('turma_id', filterTurma);
    if (filterStatus) p.set('status_pagamento', filterStatus);
    if (filterEvent) p.set('event_id', filterEvent);
    if (filterTipo) p.set('tipo_participante', filterTipo);
    return p.toString();
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename; a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao gerar arquivo.'); }
  };

  const baixarExcel = async () => {
    const qs = buildFiltersQS();
    const evSlug = (events.find(ev => ev.id === filterEvent)?.nome || 'festa_junina').toLowerCase().replace(/\s+/g, '_');
    const date = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    await downloadBlob(`/festa-junina/vendas/export/excel${qs ? '?' + qs : ''}`, `vendas_${evSlug}_${date}.xlsx`);
  };

  const imprimirRelatorio = async () => {
    const qs = buildFiltersQS();
    try {
      const res = await api.get(`/festa-junina/vendas/print${qs ? '?' + qs : ''}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const win = window.open(blobUrl, '_blank');
      if (win) setTimeout(() => { try { win.focus(); win.print(); } catch (_) { /* noop */ } }, 800);
      else toast.info('Pop-up bloqueado. Permita pop-ups para abrir o relatório.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao gerar PDF.'); }
  };

  // Fichas Antecipadas — ações isoladas
  const baixarEntregaFichas = async (sale) => {
    if (!sale?.has_fichas) return;
    if (!window.confirm(`Confirmar entrega das fichas de ${sale.aluno_nome}?`)) return;
    try {
      await api.post(`/festa-junina/sales/${sale.id}/fichas/entregar`);
      toast.success('Entrega das fichas baixada.');
      await loadAll(); await loadSales();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao baixar entrega.'); }
  };

  const s = stats?.stats || {};
  return (
    <div className="space-y-5">
      <div className="rounded-xl overflow-hidden bg-gradient-to-br from-purple-700 via-purple-800 to-emerald-700 text-white">
        <Bandeirinhas className="w-full h-7" />
        <div className="px-6 py-5">
          <div className="text-xs uppercase tracking-widest text-purple-100">Festa Junina 2026 · Vendas de Convites</div>
          <h1 className="text-2xl font-bold mt-1">Painel de Vendas</h1>
          <p className="text-purple-100 italic text-sm mt-1">"{EVENTO.frase}"</p>
        </div>
        <Bandeirinhas className="w-full h-7 rotate-180" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Vendas', s.total_vendas || 0, 'bg-white'],
          ['Acessos vendidos', s.acessos_total || 0, 'bg-white'],
          ['Acessos pagantes', s.acessos_pagantes || 0, 'bg-emerald-50 border-emerald-200'],
          ['Acessos grátis', s.acessos_gratuitos || 0, 'bg-amber-50 border-amber-200'],
          ['Gratuitos família', s.acessos_gratuitos_familia || 0, 'bg-amber-100 border-amber-300'],
          ['Valor de gratuidades', `R$ ${(s.valor_gratuidades || 0).toFixed(2)}`, 'bg-amber-100 border-amber-300'],
          ['Valor total', `R$ ${(s.valor_total || 0).toFixed(2)}`, 'bg-purple-50 border-purple-200'],
          ['Valor pago', `R$ ${(s.valor_pago || 0).toFixed(2)}`, 'bg-emerald-50 border-emerald-200'],
          ['Valor pendente', `R$ ${(s.valor_pendente || 0).toFixed(2)}`, 'bg-rose-50 border-rose-200'],
          ['Check-in feito', `${s.checkados || 0} / ${s.acessos_total || 0}`, 'bg-white'],
        ].map(([label, val, cls], i) => (
          <div key={i} className={`rounded-lg border p-3 shadow-sm ${cls}`} data-testid={`fj-sale-stat-${i}`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-600">{label}</div>
            <div className="text-xl font-bold text-slate-800">{val}</div>
          </div>
        ))}
      </div>

      {/* Por tipo de participante (Alunos vs Externos) — bloco isolado */}
      {stats?.por_tipo && (
        <div className="bg-blue-50/40 border border-blue-200 rounded-xl p-3" data-testid="fj-stats-por-tipo">
          <div className="text-xs uppercase tracking-wide text-blue-800 font-semibold mb-2">Alunos vs Público Externo</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border bg-white p-3"><div className="text-[11px] uppercase text-slate-600">Convites Alunos</div><div className="text-xl font-bold">{stats.por_tipo.aluno?.acessos || 0}</div></div>
            <div className="rounded-lg border bg-white p-3"><div className="text-[11px] uppercase text-slate-600">Convites Externos</div><div className="text-xl font-bold">{stats.por_tipo.externo?.acessos || 0}</div></div>
            <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3"><div className="text-[11px] uppercase text-slate-600">Receita Alunos</div><div className="text-lg font-bold text-emerald-800">R$ {Number(stats.por_tipo.aluno?.valor_total || 0).toFixed(2)}</div></div>
            <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3"><div className="text-[11px] uppercase text-slate-600">Receita Externos</div><div className="text-lg font-bold text-emerald-800">R$ {Number(stats.por_tipo.externo?.valor_total || 0).toFixed(2)}</div></div>
            <div className="rounded-lg border bg-amber-50 border-amber-200 p-3"><div className="text-[11px] uppercase text-slate-600">Fichas Alunos</div><div className="text-lg font-bold text-amber-800">R$ {Number(stats.por_tipo.aluno?.fichas_valor || 0).toFixed(2)}</div></div>
            <div className="rounded-lg border bg-amber-50 border-amber-200 p-3"><div className="text-[11px] uppercase text-slate-600">Fichas Externos</div><div className="text-lg font-bold text-amber-800">R$ {Number(stats.por_tipo.externo?.fichas_valor || 0).toFixed(2)}</div></div>
            <div className="rounded-lg border bg-white p-3"><div className="text-[11px] uppercase text-slate-600">Vendas Alunos</div><div className="text-xl font-bold">{stats.por_tipo.aluno?.vendas || 0}</div></div>
            <div className="rounded-lg border bg-white p-3"><div className="text-[11px] uppercase text-slate-600">Vendas Externos</div><div className="text-xl font-bold">{stats.por_tipo.externo?.vendas || 0}</div></div>
          </div>
        </div>
      )}

      {/* Fichas Antecipadas — Cards isolados (não somam com convites) */}
      <div className="bg-amber-50/40 border border-amber-200 rounded-xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-amber-800 font-semibold">Fichas Antecipadas</div>
          <Button size="sm" onClick={() => setOpenFichasOnly(true)} className="bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="fj-fichas-only-open">
            <Plus className="w-3.5 h-3.5" />Lançar fichas (sem convite)
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Valor vendido em fichas', `R$ ${Number(s.fichas_valor_vendido || 0).toFixed(2)}`, 'bg-white border-amber-200'],
            ['Fichas pagas', `${s.fichas_pagas_qtd || 0} · R$ ${Number(s.fichas_pagas_valor || 0).toFixed(2)}`, 'bg-emerald-50 border-emerald-200'],
            ['Fichas pendentes', `${s.fichas_pendentes_qtd || 0} · R$ ${Number(s.fichas_pendentes_valor || 0).toFixed(2)}`, 'bg-rose-50 border-rose-200'],
            ['Fichas entregues', `${s.fichas_entregues_qtd || 0} / ${s.fichas_total_registros || 0}`, 'bg-white border-amber-200'],
          ].map(([label, val, cls], i) => (
            <div key={i} className={`rounded-lg border p-3 shadow-sm ${cls}`} data-testid={`fj-fichas-stat-${i}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-600">{label}</div>
              <div className="text-lg font-bold text-slate-800">{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Form Nova Venda */}
      <div className="bg-white border rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 pb-1">
          <Plus className="w-4 h-4 text-purple-600" />
          <h2 className="font-semibold text-slate-800">Nova Venda</h2>
          <span className="ml-auto text-[11px] text-slate-500">Próximo número: <strong className="font-mono">#{String(stats?.proximo_numero || 1).padStart(4,'0')}</strong> de {stats?.limite_global || 5000}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Evento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={eventoId} onChange={(e) => setEventoId(e.target.value)} data-testid="fj-sale-evento">
              {events.map((ev) => (<option key={ev.id} value={ev.id}>{ev.nome}</option>))}
            </select>
          </div>
          <div className="col-span-full bg-purple-50 border border-purple-200 rounded p-2 flex items-center gap-4 text-sm" data-testid="fj-sale-tipo">
            <span className="text-xs font-semibold uppercase text-purple-700">Tipo de participante:</span>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" name="fj-tipo" value="aluno" checked={tipoParticipante === 'aluno'} onChange={() => setTipoParticipante('aluno')} data-testid="fj-tipo-aluno" />
              <span>Aluno da Escola</span>
            </label>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" name="fj-tipo" value="externo" checked={tipoParticipante === 'externo'} onChange={() => setTipoParticipante('externo')} data-testid="fj-tipo-externo" />
              <span>Público Externo</span>
            </label>
          </div>

          {tipoParticipante === 'aluno' ? (
            <>
              <div>
                <Label className="text-xs">Turma</Label>
                <select className="border rounded w-full px-2 py-2 text-sm" value={turmaId} onChange={(e) => setTurmaId(e.target.value)} data-testid="fj-sale-turma">
                  <option value="">Selecione…</option>
                  {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Aluno</Label>
                <select className="border rounded w-full px-2 py-2 text-sm" value={alunoId} onChange={(e) => setAlunoId(e.target.value)} disabled={!turmaId} data-testid="fj-sale-aluno">
                  <option value="">{turmaId ? 'Selecione…' : 'Escolha a turma primeiro'}</option>
                  {students.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">Nome do convidado *</Label>
                <Input value={extNome} onChange={(e) => setExtNome(e.target.value)} data-testid="fj-ext-nome" placeholder="Nome do convidado" />
              </div>
              <div>
                <Label className="text-xs">Telefone (opcional)</Label>
                <Input value={extTelefone} onChange={(e) => setExtTelefone(e.target.value)} data-testid="fj-ext-telefone" placeholder="(11) 99999-0000" />
              </div>
              <div>
                <Label className="text-xs">CPF (opcional)</Label>
                <Input value={extCpf} onChange={(e) => setExtCpf(e.target.value)} data-testid="fj-ext-cpf" placeholder="000.000.000-00" />
              </div>
              <div>
                <Label className="text-xs">E-mail (opcional)</Label>
                <Input value={extEmail} onChange={(e) => setExtEmail(e.target.value)} data-testid="fj-ext-email" placeholder="email@dominio.com" />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Quantidade total</Label>
            <Input type="number" min="1" max="200" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} data-testid="fj-sale-qtd" />
          </div>
          <div>
            <Label className="text-xs">Gratuidade família (+filho)</Label>
            <Input
              type="number" min="0" max="20"
              value={gratuidadeExtra}
              onChange={(e) => setGratuidadeExtra(e.target.value)}
              disabled={tipoParticipante !== 'aluno' || !familyInfo || (familyInfo?.total_familia || 1) < 2}
              data-testid="fj-sale-gratuidade-extra"
              title={tipoParticipante !== 'aluno' ? 'Apenas para alunos da escola' : ((familyInfo?.total_familia || 1) < 2 ? 'Disponível somente para famílias com mais de 1 filho' : 'Convites adicionais gratuitos para esta família')}
            />
          </div>
          <div>
            <Label className="text-xs">Responsável (opcional)</Label>
            <Input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} placeholder="Nome do responsável" />
          </div>
          <div>
            <Label className="text-xs">Forma de pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={forma} onChange={(e) => setForma(e.target.value)} data-testid="fj-sale-forma">
              {FORMAS.map((f) => (<option key={f} value={f}>{f}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Status do pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={statusPg} onChange={(e) => setStatusPg(e.target.value)} data-testid="fj-sale-status">
              {STATUS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button onClick={gerarVenda} disabled={creating || !alunoId || !turmaId} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 w-full" data-testid="fj-sale-criar">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Gerar venda
            </Button>
          </div>
        </div>

        {familyInfo && (familyInfo.total_familia || 1) > 1 && (
          <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-800 flex items-center gap-2" data-testid="fj-family-alert">
            <AlertTriangle className="w-4 h-4" />
            <strong>Família com {familyInfo.total_familia} filhos</strong> — gratuidade disponível ({familyInfo.irmaos.map(i => i.name).join(', ')}). Preencha "Gratuidade família" para aplicar convites adicionais sem custo.
          </div>
        )}

        {preview && (
          <div className="bg-slate-50 border rounded p-3 text-sm grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="fj-sale-preview">
            <div><span className="text-slate-500 text-xs">Aluno (grátis)</span><div className="font-medium">{alunoSel?.name || '—'}</div></div>
            <div><span className="text-slate-500 text-xs">Pagantes</span><div className="font-semibold text-purple-700">{preview.quantidade_pagante}</div></div>
            <div><span className="text-slate-500 text-xs">Gratuitos (família)</span><div className="font-semibold text-amber-700">{preview.quantidade_gratuita_extra || 0}</div></div>
            <div><span className="text-slate-500 text-xs">Valor total</span><div className="font-semibold text-emerald-700">R$ {Number(preview.valor_total).toFixed(2)}</div></div>
            <div><span className="text-slate-500 text-xs">Numeração que será gerada</span><div className="font-mono font-semibold">#{String(preview.primeiro_numero).padStart(4,'0')} – #{String(preview.ultimo_numero).padStart(4,'0')}</div></div>
            {preview.ultrapassa_limite && (
              <div className="col-span-full text-rose-700 text-xs flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Ultrapassa o limite global de 5000.</div>
            )}
          </div>
        )}
      </div>

      {/* Filtros + Toolbar */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <Search className="w-4 h-4 text-slate-500" />
          <Input placeholder="Buscar por aluno, responsável, turma…" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadSales(); }} />
        </div>
        <select className="border rounded px-2 py-2 text-sm" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} data-testid="fj-filter-event">
          <option value="">Todos os eventos</option>
          {events.map((ev) => (<option key={ev.id} value={ev.id}>{ev.nome}</option>))}
        </select>
        <select className="border rounded px-2 py-2 text-sm" value={filterTurma} onChange={(e) => setFilterTurma(e.target.value)}>
          <option value="">Todas as turmas</option>
          {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <select className="border rounded px-2 py-2 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">Todos status</option>
          {STATUS.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <select className="border rounded px-2 py-2 text-sm" value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} data-testid="fj-filter-tipo">
          <option value="">Todos (alunos+externos)</option>
          <option value="aluno">Apenas alunos</option>
          <option value="externo">Apenas externos</option>
        </select>
        <Button size="sm" variant="outline" onClick={loadSales} className="gap-1"><RefreshCw className="w-3.5 h-3.5" />Atualizar</Button>
        <Button size="sm" variant="outline" onClick={baixarExcel} className="gap-1 border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="fj-export-xlsx"><Download className="w-3.5 h-3.5" />Baixar Excel</Button>
        <Button size="sm" variant="outline" onClick={imprimirRelatorio} className="gap-1 border-purple-500 text-purple-700 hover:bg-purple-50" data-testid="fj-print-report"><Printer className="w-3.5 h-3.5" />Imprimir relatório</Button>
        <Button size="sm" variant="outline" onClick={exportar} className="gap-1"><Download className="w-3.5 h-3.5" />Exportar CSV</Button>
        <Button size="sm" variant="outline" onClick={resetEventoCompleto} className="gap-1 border-rose-500 text-rose-700 hover:bg-rose-50" data-testid="fj-reset-event"><Trash2 className="w-3.5 h-3.5" />Zerar evento</Button>
      </div>

      {/* Vendas por turma */}
      {(stats?.por_turma || []).length > 0 && (
        <div className="bg-white border rounded-xl p-3">
          <div className="text-xs uppercase tracking-wide text-slate-600 mb-2">Vendas por turma</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(stats.por_turma || []).map((t, i) => (
              <div key={i} className="rounded border bg-slate-50 p-2 text-xs">
                <div className="font-semibold text-slate-800">{t.turma_name}</div>
                <div className="text-slate-600">{t.vendas} vendas · {t.acessos} acessos</div>
                <div className="text-emerald-700 font-semibold">R$ {(t.valor || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de Vendas */}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Aluno</th>
              <th className="text-left px-3 py-2">Turma</th>
              <th className="text-center px-3 py-2">Números</th>
              <th className="text-center px-3 py-2">Qtd</th>
              <th className="text-center px-3 py-2 text-[11px]">Pag.</th>
              <th className="text-center px-3 py-2 text-[11px]">Grátis</th>
              <th className="text-center px-3 py-2">Valor</th>
              <th className="text-center px-3 py-2">Pagto</th>
              <th className="text-center px-3 py-2">Check-in</th>
              <th className="text-center px-3 py-2 bg-amber-50 border-l border-amber-200">Fichas R$</th>
              <th className="text-center px-3 py-2 bg-amber-50">Status fichas</th>
              <th className="text-center px-3 py-2 bg-amber-50 border-r border-amber-200">Entrega fichas</th>
              <th className="text-center px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={13} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Carregando…</td></tr>)}
            {!loading && sales.length === 0 && (<tr><td colSpan={13} className="py-6 text-center text-slate-500">Nenhuma venda registrada.</td></tr>)}
            {!loading && sales.map((sale) => (
              <tr key={sale.id} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2">{sale.aluno_nome}{sale.tipo_venda === 'fichas' && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">FICHAS</span>}</td>
                <td className="px-3 py-2">{sale.turma_name || '—'}</td>
                <td className="px-3 py-2 text-center font-mono text-xs text-purple-700">{sale.primeiro_numero ? `#${String(sale.primeiro_numero).padStart(4,'0')} – #${String(sale.ultimo_numero).padStart(4,'0')}` : '—'}</td>
                <td className="px-3 py-2 text-center">{sale.quantidade_total}</td>
                <td className="px-3 py-2 text-center text-[12px] text-emerald-700 font-medium">{sale.quantidade_pagante ?? '—'}</td>
                <td className="px-3 py-2 text-center text-[12px] text-amber-700 font-medium" title={`Aluno (grátis) + ${sale.quantidade_gratuita_extra || 0} família`}>{sale.quantidade_gratuita ?? '—'}{(sale.quantidade_gratuita_extra || 0) > 0 ? ` (+${sale.quantidade_gratuita_extra})` : ''}</td>
                <td className="px-3 py-2 text-center font-semibold text-emerald-700">R$ {Number(sale.valor_total || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-center">
                  <select className="border rounded text-xs px-1 py-0.5" value={sale.status_pagamento} onChange={(e) => setStatus(sale, e.target.value)} data-testid={`fj-sale-status-${sale.id}`}>
                    {STATUS.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  <span className={sale.total_checkados > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>{sale.total_checkados}/{sale.quantidade_total}</span>
                </td>
                {/* Fichas Antecipadas — colunas isoladas */}
                <td className="px-3 py-2 text-center bg-amber-50/40 border-l border-amber-100" data-testid={`fj-fichas-valor-${sale.id}`}>
                  {sale.has_fichas ? <span className="font-semibold text-amber-800">R$ {Number(sale.fichas_valor_total || 0).toFixed(2)}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-center bg-amber-50/40 text-xs" data-testid={`fj-fichas-status-${sale.id}`}>
                  {sale.has_fichas ? (
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${sale.fichas_pagamento_status === 'pago' ? 'bg-emerald-100 text-emerald-800' : sale.fichas_pagamento_status === 'cancelado' ? 'bg-slate-200 text-slate-600' : 'bg-rose-100 text-rose-800'}`}>{sale.fichas_pagamento_status || 'pendente'}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-center bg-amber-50/40 border-r border-amber-100 text-xs" data-testid={`fj-fichas-entrega-${sale.id}`}>
                  {sale.has_fichas ? (
                    <span className={`px-1.5 py-0.5 rounded font-semibold ${sale.fichas_entrega_status === 'entregue' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{fichasEntLabel(sale.fichas_entrega_status)}</span>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <div className="flex justify-center gap-1 flex-wrap">
                    {sale.tipo_venda !== 'fichas' && (
                      <Button size="sm" variant="outline" onClick={() => setEditTarget(sale)} title="Editar venda" data-testid={`fj-sale-edit-${sale.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                    )}
                    {sale.tipo_venda !== 'fichas' && (
                      <Button size="sm" variant="outline" onClick={() => setQrTarget(sale)} title="QR Code" data-testid={`fj-sale-qr-${sale.id}`}><QrCode className="w-3.5 h-3.5" /></Button>
                    )}
                    {sale.tipo_venda !== 'fichas' && (
                      <Button size="sm" variant="outline"
                        onClick={() => togglePublicarConnect(sale)}
                        title={sale.publicado_dna_connect === false ? 'Publicar no DNA Connect' : 'Ocultar do DNA Connect'}
                        data-testid={`fj-sale-publish-${sale.id}`}>
                        <span className={sale.publicado_dna_connect === false ? 'text-slate-400' : 'text-emerald-600'} style={{fontSize:11,fontWeight:700}}>
                          {sale.publicado_dna_connect === false ? 'OCULTO' : 'CONNECT'}
                        </span>
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setPrintTarget(sale)} title="Imprimir comprovante" data-testid={`fj-sale-print-${sale.id}`}><Printer className="w-3.5 h-3.5" /></Button>
                    {sale.tipo_venda !== 'fichas' && (
                      <Link to={`/v2/festa-junina/checkin/${sale.token}`}>
                        <Button size="sm" variant="outline" title="Check-in" data-testid={`fj-sale-checkin-${sale.id}`}><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /></Button>
                      </Link>
                    )}
                    {/* Fichas Antecipadas — botões isolados */}
                    <Button size="sm" variant="outline" onClick={() => setFichasTarget(sale)} title={sale.has_fichas ? 'Editar fichas' : 'Adicionar fichas'} className="border-amber-400 hover:bg-amber-50" data-testid={`fj-fichas-edit-${sale.id}`}>
                      <span className="text-[10px] font-bold text-amber-700">{sale.has_fichas ? 'FICHAS✎' : '+FICHAS'}</span>
                    </Button>
                    {sale.has_fichas && sale.fichas_entrega_status !== 'entregue' && (
                      <Button size="sm" variant="outline" onClick={() => baixarEntregaFichas(sale)} title="Baixar entrega das fichas" className="border-emerald-500 hover:bg-emerald-50" data-testid={`fj-fichas-entregar-${sale.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-[10px] font-bold text-emerald-700 ml-0.5">ENT</span>
                      </Button>
                    )}
                    {sale.status_pagamento !== 'cancelado' && sale.tipo_venda !== 'fichas' && (
                      <Button size="sm" variant="outline" title="Cancelar venda" onClick={() => cancelSale(sale)} data-testid={`fj-sale-cancel-${sale.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="outline" title="Excluir definitivo" onClick={() => hardDeleteSale(sale)} data-testid={`fj-sale-hard-${sale.id}`}>
                      <XCircle className="w-3.5 h-3.5 text-rose-800" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-3 py-2 text-xs text-slate-600">Total de vendas: {salesTotal}</div>
      </div>

      {qrTarget && <QrSaleDialog sale={qrTarget} onClose={() => setQrTarget(null)} />}
      {printTarget && <PrintSaleDialog sale={printTarget} onClose={() => setPrintTarget(null)} />}
      {editTarget && <EditSaleDialog sale={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); loadAll(); loadSales(); }} />}
      {fichasTarget && <FichasDialog sale={fichasTarget} onClose={() => setFichasTarget(null)} onSaved={() => { setFichasTarget(null); loadAll(); loadSales(); }} />}
      {openFichasOnly && <FichasOnlyDialog events={events} classes={classes} defaultEventId={eventoId} onClose={() => setOpenFichasOnly(false)} onSaved={() => { setOpenFichasOnly(false); loadAll(); loadSales(); }} />}
    </div>
  );
}

function QrSaleDialog({ sale, onClose }) {
  const url = saleCheckinUrl(sale.token);
  const compartilhar = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Convite ${sale.aluno_nome}`, text: `Festa Junina · ${sale.quantidade_total} acessos · #${String(sale.primeiro_numero).padStart(4,'0')}-#${String(sale.ultimo_numero).padStart(4,'0')}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copiado.');
      }
    } catch { /* noop */ }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader><DialogTitle>QR de Check-in — {sale.aluno_nome}</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-3 py-4">
          <QRCodeCanvas value={url} size={220} level="M" includeMargin />
          <div className="text-xs text-slate-600 text-center break-all">{url}</div>
          <div className="text-sm">Acessos: <strong>{sale.quantidade_total}</strong> · Números <span className="font-mono">#{String(sale.primeiro_numero).padStart(4,'0')}–#{String(sale.ultimo_numero).padStart(4,'0')}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={compartilhar} className="gap-1" data-testid="fj-qr-share"><Copy className="w-4 h-4" />Compartilhar / copiar link</Button>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditSaleDialog({ sale, onClose, onSaved }) {
  const [form, setForm] = useState({
    status_pagamento: sale.status_pagamento || 'pendente',
    forma_pagamento: sale.forma_pagamento || 'pix',
    responsavel_nome: sale.responsavel_nome || '',
    observacao: sale.observacao || '',
    quantidade_total: sale.quantidade_total,
    quantidade_gratuita_extra: sale.quantidade_gratuita_extra || 0,
  });
  const [saving, setSaving] = useState(false);
  const novaQtd = Number(form.quantidade_total || 0);
  const atual = sale.quantidade_total || 0;
  const delta = novaQtd - atual;
  const checkados = sale.total_checkados || 0;
  const reducaoBloqueada = novaQtd < checkados;
  const baseGratuita = (sale.aluno_id || sale.aluno_nome) ? 1 : 0;
  const novosGratuitos = baseGratuita + (Number(form.quantidade_gratuita_extra) || 0);
  const novosPagantes = Math.max(0, novaQtd - novosGratuitos);
  const novoValor = novosPagantes * (sale.valor_unitario || 12);

  const salvar = async () => {
    if (reducaoBloqueada) {
      toast.error(`Não é possível reduzir abaixo dos convites já utilizados (check-ins: ${checkados}).`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        status_pagamento: form.status_pagamento,
        forma_pagamento: form.forma_pagamento,
        responsavel_nome: form.responsavel_nome || null,
        observacao: form.observacao || null,
      };
      if (novaQtd !== atual) payload.quantidade_total = novaQtd;
      if (Number(form.quantidade_gratuita_extra) !== Number(sale.quantidade_gratuita_extra || 0)) {
        payload.quantidade_gratuita_extra = Number(form.quantidade_gratuita_extra) || 0;
      }
      await api.patch(`/festa-junina/sales/${sale.id}`, payload);
      toast.success(delta > 0 ? `Venda atualizada (+${delta} acessos, QR renovado).`
        : delta < 0 ? `Venda atualizada (${delta} acessos descartados, QR renovado).`
        : 'Venda atualizada.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader><DialogTitle>Editar venda · {sale.aluno_nome}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Status</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.status_pagamento} onChange={(e) => setForm({ ...form, status_pagamento: e.target.value })}>
              {STATUS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div>
            <Label>Forma de pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.forma_pagamento} onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}>
              {FORMAS.map((f) => (<option key={f} value={f}>{f}</option>))}
            </select>
          </div>
          <div className="col-span-2"><Label>Responsável</Label><Input value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} /></div>
          <div className="col-span-2"><Label>Observação</Label><Textarea rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} /></div>
          <div className="col-span-2 bg-slate-50 border rounded p-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Convites totais (atual: {atual})</Label>
                <Input type="number" min={1} max={200} value={form.quantidade_total} onChange={(e) => setForm({ ...form, quantidade_total: e.target.value })} data-testid="fj-edit-qtd" />
              </div>
              <div>
                <Label className="text-xs">Gratuidade família (atual: {sale.quantidade_gratuita_extra || 0})</Label>
                <Input type="number" min={0} max={20} value={form.quantidade_gratuita_extra} onChange={(e) => setForm({ ...form, quantidade_gratuita_extra: e.target.value })} data-testid="fj-edit-gratuidade" />
              </div>
            </div>
            <div className="text-[11px] text-slate-600 grid grid-cols-3 gap-2 border-t pt-2">
              <div>Pagantes: <strong>{novosPagantes}</strong></div>
              <div>Gratuitos: <strong>{novosGratuitos}</strong> ({baseGratuita} aluno + {form.quantidade_gratuita_extra || 0} família)</div>
              <div>Valor: <strong className="text-emerald-700">R$ {novoValor.toFixed(2)}</strong></div>
            </div>
            {reducaoBloqueada && (
              <div className="text-rose-700 text-xs flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Não é possível reduzir abaixo dos convites já utilizados (check-ins: {checkados}).</div>
            )}
            {delta > 0 && <div className="text-[11px] text-amber-700">+{delta} acessos serão adicionados, QR atual será invalidado e um novo gerado.</div>}
            {delta < 0 && !reducaoBloqueada && <div className="text-[11px] text-amber-700">{Math.abs(delta)} convites do final serão descartados; QR será renovado.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving || reducaoBloqueada} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="fj-edit-save">{saving ? 'Salvando…' : 'Salvar alterações'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrintSaleDialog({ sale, onClose }) {
  useEffect(() => { setTimeout(() => window.print(), 400); }, []);
  const url = saleCheckinUrl(sale.token);
  const temConvites = (sale.numeros || []).length > 0;
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl bg-white">
        <DialogHeader><DialogTitle>Imprimir comprovante — {sale.aluno_nome}</DialogTitle></DialogHeader>
        <style>{`@media print { .no-print{display:none!important} @page{size:A4;margin:8mm} .sheet{display:grid;grid-template-columns:1fr 1fr;gap:6mm} .card{border:1px dashed #94a3b8;border-radius:8px;padding:8px;page-break-inside:avoid} .pb{page-break-after:always} }`}</style>
        <div className="bg-white">
          {temConvites && Array.from({ length: Math.ceil((sale.numeros || []).length / 4) }).map((_, pageIdx) => (
            <div key={pageIdx} className={`sheet grid grid-cols-2 gap-3 ${pageIdx < Math.ceil(sale.numeros.length/4)-1 ? 'pb' : ''}`}>
              {(sale.numeros || []).slice(pageIdx*4, pageIdx*4+4).map((n) => (
                <div key={n} className="card border border-dashed rounded p-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <div style={{ fontSize: 10, color:'#6b7280', textTransform:'uppercase', letterSpacing:1 }}>Festa Junina · {EVENTO.data}</div>
                      <div style={{ fontFamily:'monospace', fontSize:24, fontWeight:700, color:'#7B3F98' }}>#{String(n).padStart(4,'0')}</div>
                      <div style={{ fontSize:11 }}><strong>{sale.aluno_nome}</strong>{sale.turma_name ? ` · ${sale.turma_name}` : ''}</div>
                      <div style={{ fontSize:10, color:'#475569' }}>Resp.: {sale.responsavel_nome || '—'}</div>
                      <div style={{ fontSize:9, color:'#0f766e', marginTop:4, fontStyle:'italic' }}>"{EVENTO.frase}"</div>
                    </div>
                    <QRCodeCanvas value={url} size={80} level="M" />
                  </div>
                  <div style={{ fontSize:9, color:'#475569', marginTop:4, borderTop:'1px solid #e2e8f0', paddingTop:4 }}>
                    {EVENTO.local} · {EVENTO.horario} · Pagante: {EVENTO.valor}
                  </div>
                  {/* Canhoto destacável (via escola) */}
                  <div style={{ borderTop:'1px dashed #94a3b8', marginTop:6, paddingTop:4, display:'flex', justifyContent:'space-between', fontSize:10 }}>
                    <span><strong>Via escola</strong> · #{String(n).padStart(4,'0')}</span>
                    <span>{sale.aluno_nome}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* CONVITES — resumo separado (pagantes / gratuitos / total / valor) */}
          {temConvites && (
            <div className="mt-4" data-testid="fj-print-convites-section">
              <div style={{ border:'2px solid #7B3F98', borderRadius:8, padding:12, background:'#faf5ff' }}>
                <div style={{ fontSize:10, color:'#5b21b6', textTransform:'uppercase', letterSpacing:1.5, fontWeight:700, marginBottom:6 }}>
                  CONVITES
                </div>
                <div style={{ fontSize:11, color:'#1f2937', lineHeight:1.7 }}>
                  <div><strong>Convites pagantes:</strong> {sale.quantidade_pagante ?? 0}</div>
                  <div><strong>Convites gratuitos:</strong> {sale.quantidade_gratuita ?? 0}{(sale.quantidade_gratuita_extra || 0) > 0 ? ` (aluno + ${sale.quantidade_gratuita_extra} família)` : ''}</div>
                  <div><strong>Total de convites:</strong> {sale.quantidade_total}</div>
                  <div><strong>Valor pago:</strong> R$ {Number(sale.valor_total || 0).toFixed(2)} ({sale.status_pagamento || 'pendente'} · {sale.forma_pagamento || '—'})</div>
                </div>
              </div>
            </div>
          )}

          {/* FICHAS ANTECIPADAS — seção totalmente separada (não mistura com convites) */}
          {sale.has_fichas && (
            <div className="mt-4" data-testid="fj-print-fichas-section">
              <div style={{ border:'2px solid #d97706', borderRadius:8, padding:12, background:'#fffbeb' }}>
                <div style={{ fontSize:10, color:'#92400e', textTransform:'uppercase', letterSpacing:1.5, fontWeight:700, marginBottom:6 }}>
                  FICHAS ANTECIPADAS
                </div>
                <div style={{ fontSize:11, color:'#1f2937', lineHeight:1.7 }}>
                  <div><strong>Aluno:</strong> {sale.aluno_nome}{sale.turma_name ? ` · ${sale.turma_name}` : ''}</div>
                  <div><strong>Valor:</strong> R$ {Number(sale.fichas_valor_total || 0).toFixed(2)}</div>
                  <div><strong>Pagamento:</strong> {sale.fichas_pagamento_forma || '—'} · <strong>Status:</strong> {sale.fichas_pagamento_status || 'pendente'}</div>
                  <div><strong>Entrega:</strong> {fichasEntLabel(sale.fichas_entrega_status)}{sale.fichas_entregue_at ? ` (em ${new Date(sale.fichas_entregue_at).toLocaleString('pt-BR')})` : ''}</div>
                  {sale.fichas_observacao && <div><strong>Obs.:</strong> {sale.fichas_observacao}</div>}
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="no-print">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={() => window.print()} className="bg-purple-600 text-white"><Printer className="w-4 h-4 mr-1" />Imprimir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// FICHAS ANTECIPADAS — Dialogs isolados (não mexem na venda de convite)
// ──────────────────────────────────────────────────────────────────
function FichasDialog({ sale, onClose, onSaved }) {
  const [form, setForm] = useState({
    fichas_valor_total: sale.fichas_valor_total ?? '',
    fichas_pagamento_forma: sale.fichas_pagamento_forma || 'pix',
    fichas_pagamento_status: sale.fichas_pagamento_status || 'pendente',
    fichas_entrega_status: sale.fichas_entrega_status || 'nao_entregue',
    fichas_observacao: sale.fichas_observacao || '',
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!sale.has_fichas;
  const salvar = async () => {
    const val = Number(form.fichas_valor_total);
    if (!val || val <= 0) { toast.error('Informe um valor maior que zero.'); return; }
    setSaving(true);
    try {
      await api.patch(`/festa-junina/sales/${sale.id}/fichas`, {
        fichas_valor_total: val,
        fichas_pagamento_forma: form.fichas_pagamento_forma,
        fichas_pagamento_status: form.fichas_pagamento_status,
        fichas_entrega_status: form.fichas_entrega_status,
        fichas_observacao: form.fichas_observacao || null,
      });
      toast.success(isEdit ? 'Fichas atualizadas.' : 'Fichas anexadas à venda.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar fichas.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-amber-700">{isEdit ? 'Editar Fichas Antecipadas' : 'Adicionar Fichas Antecipadas'}</span>
          </DialogTitle>
          <div className="text-xs text-slate-500">Aluno: <strong>{sale.aluno_nome}</strong>{sale.turma_name ? ` · ${sale.turma_name}` : ''}</div>
          {sale.tipo_venda !== 'fichas' && (sale.numeros || []).length > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Esta venda possui convites. As fichas serão anexadas SEM alterar a venda do convite.</div>
          )}
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Valor das fichas (R$)</Label>
            <Input type="number" step="0.01" min="0.01" value={form.fichas_valor_total} onChange={(e) => setForm({ ...form, fichas_valor_total: e.target.value })} data-testid="fj-fichas-valor-input" />
          </div>
          <div>
            <Label>Forma de pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.fichas_pagamento_forma} onChange={(e) => setForm({ ...form, fichas_pagamento_forma: e.target.value })} data-testid="fj-fichas-forma">
              {FORMAS.map((f) => (<option key={f} value={f}>{f}</option>))}
            </select>
          </div>
          <div>
            <Label>Status pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.fichas_pagamento_status} onChange={(e) => setForm({ ...form, fichas_pagamento_status: e.target.value })} data-testid="fj-fichas-pag-status">
              {FICHAS_PAG_STATUS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div className="col-span-2">
            <Label>Status entrega</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.fichas_entrega_status} onChange={(e) => setForm({ ...form, fichas_entrega_status: e.target.value })} data-testid="fj-fichas-ent-status">
              {FICHAS_ENT_STATUS.map((s) => (<option key={s} value={s}>{fichasEntLabel(s)}</option>))}
            </select>
          </div>
          <div className="col-span-2">
            <Label>Observação</Label>
            <Textarea rows={2} value={form.fichas_observacao} onChange={(e) => setForm({ ...form, fichas_observacao: e.target.value })} data-testid="fj-fichas-obs" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fj-fichas-save">
            {saving ? 'Salvando…' : (isEdit ? 'Salvar fichas' : 'Anexar fichas')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FichasOnlyDialog({ events, classes, defaultEventId, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({
    event_id: defaultEventId || (events?.[0]?.id || ''),
    turma_id: '',
    aluno_id: '',
    responsavel_nome: '',
    fichas_valor_total: '',
    fichas_pagamento_forma: 'pix',
    fichas_pagamento_status: 'pendente',
    fichas_entrega_status: 'nao_entregue',
    fichas_observacao: '',
  });
  const [saving, setSaving] = useState(false);
  const turmaSel = useMemo(() => (classes || []).find((c) => c.id === form.turma_id), [classes, form.turma_id]);
  const alunoSel = useMemo(() => students.find((s) => s.id === form.aluno_id), [students, form.aluno_id]);

  useEffect(() => {
    if (!form.turma_id) { setStudents([]); return; }
    (async () => {
      try {
        const res = await api.get(`/students?class_id=${form.turma_id}&active=true&page_size=500`);
        const arr = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.students || []);
        setStudents(arr);
      } catch { setStudents([]); }
    })();
  }, [form.turma_id]);

  const salvar = async () => {
    const val = Number(form.fichas_valor_total);
    if (!val || val <= 0) { toast.error('Informe um valor maior que zero.'); return; }
    if (!alunoSel?.name) { toast.error('Selecione o aluno.'); return; }
    setSaving(true);
    try {
      await api.post('/festa-junina/sales/fichas', {
        event_id: form.event_id || undefined,
        aluno_id: alunoSel?.id, aluno_nome: alunoSel?.name,
        turma_id: turmaSel?.id, turma_name: turmaSel?.name,
        responsavel_nome: form.responsavel_nome || null,
        fichas_valor_total: val,
        fichas_pagamento_forma: form.fichas_pagamento_forma,
        fichas_pagamento_status: form.fichas_pagamento_status,
        fichas_entrega_status: form.fichas_entrega_status,
        fichas_observacao: form.fichas_observacao || null,
      });
      toast.success('Fichas avulsas lançadas (sem convite).');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao lançar fichas.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-amber-700">Lançar Fichas Antecipadas (sem convite)</DialogTitle>
          <div className="text-xs text-slate-500">Cria um registro apenas de fichas — não gera convite, número nem QR.</div>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Evento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })}>
              {(events || []).map((ev) => (<option key={ev.id} value={ev.id}>{ev.nome}</option>))}
            </select>
          </div>
          <div>
            <Label>Turma</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.turma_id} onChange={(e) => setForm({ ...form, turma_id: e.target.value, aluno_id: '' })} data-testid="fj-fichas-only-turma">
              <option value="">Selecione…</option>
              {(classes || []).map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <Label>Aluno</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.aluno_id} onChange={(e) => setForm({ ...form, aluno_id: e.target.value })} disabled={!form.turma_id} data-testid="fj-fichas-only-aluno">
              <option value="">{form.turma_id ? 'Selecione…' : 'Escolha a turma primeiro'}</option>
              {students.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
          </div>
          <div className="col-span-2"><Label>Responsável (opcional)</Label><Input value={form.responsavel_nome} onChange={(e) => setForm({ ...form, responsavel_nome: e.target.value })} /></div>
          <div className="col-span-2">
            <Label>Valor das fichas (R$)</Label>
            <Input type="number" step="0.01" min="0.01" value={form.fichas_valor_total} onChange={(e) => setForm({ ...form, fichas_valor_total: e.target.value })} data-testid="fj-fichas-only-valor" />
          </div>
          <div>
            <Label>Forma de pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.fichas_pagamento_forma} onChange={(e) => setForm({ ...form, fichas_pagamento_forma: e.target.value })}>
              {FORMAS.map((f) => (<option key={f} value={f}>{f}</option>))}
            </select>
          </div>
          <div>
            <Label>Status pagamento</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.fichas_pagamento_status} onChange={(e) => setForm({ ...form, fichas_pagamento_status: e.target.value })}>
              {FICHAS_PAG_STATUS.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          <div className="col-span-2">
            <Label>Status entrega</Label>
            <select className="border rounded w-full px-2 py-2 text-sm" value={form.fichas_entrega_status} onChange={(e) => setForm({ ...form, fichas_entrega_status: e.target.value })}>
              {FICHAS_ENT_STATUS.map((s) => (<option key={s} value={s}>{fichasEntLabel(s)}</option>))}
            </select>
          </div>
          <div className="col-span-2"><Label>Observação</Label><Textarea rows={2} value={form.fichas_observacao} onChange={(e) => setForm({ ...form, fichas_observacao: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fj-fichas-only-save">
            {saving ? 'Salvando…' : 'Lançar fichas'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────────
// EVENTOS — CRUD inline
// ──────────────────────────────────────────────────────────────────
export function FestaJuninaEventos() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/festa-junina/events');
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const novo = () => setEditing({
    nome: '', tema: '', data: '', horario_inicio: '10:00', horario_fim: '17:00',
    local: '', valor_ingresso: 12, quantidade_maxima: 5000, ativo: true,
    permite_qr: true, permite_checkin_parcial: true, permite_aluno_gratuito: true,
    premio: '', frase: '',
  });

  const remover = async (ev) => {
    if (!window.confirm(`Arquivar "${ev.nome}"? Vendas existentes serão mantidas.`)) return;
    try { await api.delete(`/festa-junina/events/${ev.id}`); toast.success('Evento arquivado.'); load(); }
    catch (e) { toast.error('Falha ao arquivar.'); }
  };
  const duplicar = async (ev) => {
    try { await api.post(`/festa-junina/events/${ev.id}/duplicate`); toast.success('Evento duplicado.'); load(); }
    catch (e) { toast.error('Falha ao duplicar.'); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl overflow-hidden bg-gradient-to-br from-purple-700 to-emerald-700 text-white">
        <Bandeirinhas className="w-full h-6" />
        <div className="px-6 py-4">
          <div className="text-xs uppercase tracking-widest text-purple-100">DNACore · V2 · Eventos</div>
          <h1 className="text-2xl font-bold mt-1">Eventos</h1>
        </div>
        <Bandeirinhas className="w-full h-6 rotate-180" />
      </div>

      <div className="flex justify-end">
        <Button onClick={novo} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="fj-event-new">
          <Plus className="w-4 h-4" /> Novo evento
        </Button>
      </div>

      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Nome</th>
              <th className="text-left px-3 py-2">Tema</th>
              <th className="text-left px-3 py-2">Data</th>
              <th className="text-center px-3 py-2">Valor</th>
              <th className="text-center px-3 py-2">Cap.</th>
              <th className="text-center px-3 py-2">Ativo</th>
              <th className="text-center px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Carregando…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={7} className="py-6 text-center text-slate-500">Sem eventos. Crie o primeiro.</td></tr>)}
            {!loading && items.map((ev) => (
              <tr key={ev.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/v2/eventos/${ev.id}`)} data-testid={`fj-event-row-${ev.id}`}>
                <td className="px-3 py-2 font-semibold text-purple-800 underline-offset-2 hover:underline">{ev.nome}</td>
                <td className="px-3 py-2">{ev.tema || '—'}</td>
                <td className="px-3 py-2">{ev.data || '—'} {ev.horario_inicio || ''}{ev.horario_fim ? `–${ev.horario_fim}` : ''}</td>
                <td className="px-3 py-2 text-center">R$ {Number(ev.valor_ingresso || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-center">{ev.quantidade_maxima || '—'}</td>
                <td className="px-3 py-2 text-center">{ev.ativo ? <span className="text-emerald-700">Sim</span> : <span className="text-slate-400">Não</span>}</td>
                <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...ev })} title="Editar" data-testid={`fj-event-edit-${ev.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => duplicar(ev)} title="Duplicar"><Copy className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remover(ev)} title="Arquivar"><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EventDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EventDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.type === 'checkbox' ? e.target.checked : e.target.value });
  const save = async () => {
    if (!form.nome) { toast.error('Nome obrigatório.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, valor_ingresso: Number(form.valor_ingresso || 0), quantidade_maxima: Number(form.quantidade_maxima || 5000) };
      if (isEdit) await api.patch(`/festa-junina/events/${form.id}`, payload);
      else await api.post('/festa-junina/events', payload);
      toast.success(isEdit ? 'Evento atualizado.' : 'Evento criado.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao salvar.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader><DialogTitle>{isEdit ? 'Editar evento' : 'Novo evento'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Nome *</Label><Input value={form.nome || ''} onChange={set('nome')} data-testid="fj-event-nome" /></div>
          <div><Label>Tema</Label><Input value={form.tema || ''} onChange={set('tema')} /></div>
          <div><Label>Prêmio/Sorteio</Label><Input value={form.premio || ''} onChange={set('premio')} /></div>
          <div><Label>Data</Label><Input value={form.data || ''} onChange={set('data')} placeholder="20/06/2026" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Início</Label><Input value={form.horario_inicio || ''} onChange={set('horario_inicio')} placeholder="10:00" /></div>
            <div><Label>Fim</Label><Input value={form.horario_fim || ''} onChange={set('horario_fim')} placeholder="17:00" /></div>
          </div>
          <div className="col-span-2"><Label>Local</Label><Input value={form.local || ''} onChange={set('local')} /></div>
          <div><Label>Valor ingresso (R$)</Label><Input type="number" step="0.01" value={form.valor_ingresso ?? 0} onChange={set('valor_ingresso')} /></div>
          <div><Label>Capacidade máx.</Label><Input type="number" value={form.quantidade_maxima ?? 5000} onChange={set('quantidade_maxima')} /></div>
          <div className="col-span-2"><Label>Frase / descrição</Label><Textarea rows={2} value={form.frase || ''} onChange={set('frase')} /></div>
          <div className="col-span-2 flex flex-wrap gap-3 text-sm">
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!form.ativo} onChange={set('ativo')} />Ativo</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={form.published_to_family !== false} onChange={set('published_to_family')} data-testid="fj-event-publish" />Publicar no DNA Connect</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!form.permite_qr} onChange={set('permite_qr')} />Permite QR</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!form.permite_checkin_parcial} onChange={set('permite_checkin_parcial')} />Permite check-in parcial</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!form.permite_aluno_gratuito} onChange={set('permite_aluno_gratuito')} />Aluno grátis</label>
            <label className="inline-flex items-center gap-1" data-testid="fj-event-gratuidade-familia">
              <input type="checkbox" checked={!!form.gratuidade_familia_ativa} onChange={set('gratuidade_familia_ativa')} />Aplicar gratuidade para família com mais de 1 filho
            </label>
          </div>
          {form.gratuidade_familia_ativa && (
            <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-2 grid grid-cols-2 gap-2 text-sm">
              <div>
                <Label className="text-xs">Modo da gratuidade</Label>
                <select className="border rounded w-full px-2 py-2 text-sm" value={form.gratuidade_modo || 'por_familia'} onChange={set('gratuidade_modo')} data-testid="fj-event-gratuidade-modo">
                  <option value="por_familia">1 convite gratuito por família</option>
                  <option value="por_aluno_adicional">1 convite gratuito por aluno adicional</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Quantidade gratuita por família</Label>
                <Input type="number" min={1} max={20} value={form.quantidade_gratuita_por_familia ?? 1} onChange={set('quantidade_gratuita_por_familia')} data-testid="fj-event-gratuidade-qtd" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">{saving ? 'Salvando…' : 'Salvar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




// ──────────────────────────────────────────────────────────────────
// SORTEIO POR CHECK-IN — página isolada (não altera vendas/check-ins)
// ──────────────────────────────────────────────────────────────────
export function FestaJuninaSorteio() {
  const [events, setEvents] = useState([]);
  const [eventoId, setEventoId] = useState('');
  const [parts, setParts] = useState(null);
  const [historico, setHistorico] = useState({ items: [], resets: [], total_ativos: 0 });
  const [prizeName, setPrizeName] = useState('Bicicleta');
  const [drawing, setDrawing] = useState(false);
  const [resetText, setResetText] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [lastWinner, setLastWinner] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadEvents = async () => {
    try {
      const res = await api.get('/festa-junina/events');
      const arr = res.data.items || [];
      setEvents(arr);
      if (!eventoId && arr[0]) setEventoId(arr[0].id);
    } catch { /* noop */ }
  };

  const refresh = async () => {
    if (!eventoId) return;
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get(`/festa-junina/events/${eventoId}/sorteio/participantes`),
        api.get(`/festa-junina/events/${eventoId}/sorteio/historico?incluir_zerados=true`),
      ]);
      setParts(p.data);
      setHistorico(h.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao carregar.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadEvents(); }, []); // eslint-disable-line
  useEffect(() => { refresh(); }, [eventoId]); // eslint-disable-line

  const sortear = async () => {
    if (!eventoId) return;
    setDrawing(true);
    try {
      const res = await api.post(`/festa-junina/events/${eventoId}/sorteio/sortear`, { prize_name: prizeName || 'Brinde' });
      setLastWinner(res.data.winner);
      toast.success(`Ganhador #${res.data.ordem}: ${res.data.winner.aluno_nome}!`);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao sortear.'); }
    finally { setDrawing(false); }
  };

  const zerar = async () => {
    if (resetText.trim().toUpperCase() !== 'ZERAR') { toast.error('Digite ZERAR para confirmar.'); return; }
    try {
      const res = await api.post(`/festa-junina/events/${eventoId}/sorteio/reset`, { confirm: 'ZERAR' });
      toast.success(`Sorteio zerado. ${res.data.reset.qtd_zerados} ganhadores anteriores arquivados.`);
      setShowReset(false); setResetText(''); setLastWinner(null);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Falha ao zerar.'); }
  };

  const ativos = (historico.items || []).filter(i => i.active);
  const zerados = (historico.items || []).filter(i => !i.active);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-purple-600 to-emerald-600 text-white rounded-xl p-6">
        <div className="text-xs uppercase tracking-wider opacity-80">Festa Junina 2026 · Sorteio</div>
        <h1 className="text-3xl font-bold">Sorteio por Check-in</h1>
        <p className="text-sm opacity-90">Somente participantes com check-in válido entram no sorteio.</p>
      </div>

      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-2">
        <Label className="text-xs">Evento:</Label>
        <select className="border rounded px-2 py-2 text-sm" value={eventoId} onChange={(e) => setEventoId(e.target.value)} data-testid="fj-sorteio-event">
          {events.map((ev) => (<option key={ev.id} value={ev.id}>{ev.nome}</option>))}
        </select>
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="fj-sorteio-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Atualizar participantes</Button>
      </div>

      {parts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fj-sorteio-stats">
          {[
            ['Check-ins válidos', parts.total_checkins || 0, 'bg-emerald-50 border-emerald-200'],
            ['Participantes únicos', parts.total_participantes || 0, 'bg-white'],
            ['Tickets elegíveis', parts.tickets_elegiveis || 0, 'bg-purple-50 border-purple-200'],
            ['Já sorteados', parts.ja_sorteados || 0, 'bg-amber-50 border-amber-200'],
          ].map(([l, v, c], i) => (
            <div key={i} className={`rounded-lg border p-3 shadow-sm ${c}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-600">{l}</div>
              <div className="text-2xl font-bold text-slate-800">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Prêmio</Label>
            <Input value={prizeName} onChange={(e) => setPrizeName(e.target.value)} placeholder="Bicicleta" data-testid="fj-sorteio-premio" />
          </div>
          <Button onClick={sortear} disabled={drawing || !parts?.tickets_elegiveis} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="fj-sorteio-sortear">
            {drawing ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sortear ganhador
          </Button>
          <Button variant="outline" onClick={() => setShowReset(true)} className="border-rose-400 text-rose-700 hover:bg-rose-50" data-testid="fj-sorteio-zerar-open">
            <Trash2 className="w-4 h-4 mr-1" />Zerar sorteio
          </Button>
        </div>
        {(!parts?.tickets_elegiveis && ativos.length > 0) && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Todos os check-ins elegíveis já foram sorteados. Zere para começar de novo.</div>
        )}
        {lastWinner && (
          <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-4" data-testid="fj-sorteio-last-winner">
            <div className="text-xs uppercase text-emerald-700 font-bold">Último ganhador</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{lastWinner.aluno_nome}</div>
            <div className="text-sm text-slate-600">{lastWinner.turma_name || '—'} · Prêmio: <strong>{lastWinner.prize_name}</strong> · Convite #{String(lastWinner.numero_convite || '').padStart(4,'0')}</div>
            <div className="text-[11px] text-slate-500 mt-1">Sorteado em {new Date(lastWinner.drawn_at).toLocaleString('pt-BR')} por {lastWinner.drawn_by_name}</div>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-slate-700">Histórico de sorteios</div>
          <div className="text-xs text-slate-500">{ativos.length} ativos · {zerados.length} zerados · {historico.resets?.length || 0} resets</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fj-sorteio-historico">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-center px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Ganhador</th>
                <th className="text-left px-3 py-2">Turma</th>
                <th className="text-center px-3 py-2">Convite</th>
                <th className="text-left px-3 py-2">Premio</th>
                <th className="text-left px-3 py-2">Quando</th>
                <th className="text-left px-3 py-2">Sorteado por</th>
                <th className="text-center px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {historico.items.length === 0 && (<tr><td colSpan={8} className="py-6 text-center text-slate-500">Nenhum sorteio realizado.</td></tr>)}
              {historico.items.map((w) => (
                <tr key={w.id} className={`border-t ${w.active ? '' : 'bg-slate-50 text-slate-400 line-through'}`}>
                  <td className="px-3 py-2 text-center font-mono">{w.ordem}</td>
                  <td className="px-3 py-2 font-semibold">{w.aluno_nome}</td>
                  <td className="px-3 py-2">{w.turma_name || '—'}</td>
                  <td className="px-3 py-2 text-center font-mono text-xs text-purple-700">#{String(w.numero_convite || '').padStart(4,'0')}</td>
                  <td className="px-3 py-2">{w.prize_name}</td>
                  <td className="px-3 py-2 text-xs">{new Date(w.drawn_at).toLocaleString('pt-BR')}</td>
                  <td className="px-3 py-2 text-xs">{w.drawn_by_name}</td>
                  <td className="px-3 py-2 text-center text-xs">{w.active ? <span className="text-emerald-600 font-semibold">ATIVO</span> : <span>ZERADO</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(historico.resets || []).length > 0 && (
          <div className="px-4 py-3 border-t text-xs text-slate-600">
            <strong>Resets:</strong>
            <ul className="mt-1 space-y-1">
              {historico.resets.map((r) => (
                <li key={r.id}>{new Date(r.at).toLocaleString('pt-BR')} · {r.by_user_name} · {r.qtd_zerados} ganhadores zerados</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showReset && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) { setShowReset(false); setResetText(''); } }}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle className="text-rose-700">Zerar sorteio</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-rose-800 text-xs">
                Esta ação arquiva todos os ganhadores deste evento e permite que todos os check-ins voltem a participar. Vendas e check-ins NÃO são alterados.
              </div>
              <Label className="text-xs">Digite <strong>ZERAR</strong> para confirmar</Label>
              <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="ZERAR" data-testid="fj-sorteio-reset-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowReset(false); setResetText(''); }}>Cancelar</Button>
              <Button onClick={zerar} disabled={resetText.trim().toUpperCase() !== 'ZERAR'} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="fj-sorteio-zerar-confirm">Zerar sorteio</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
